import * as z from 'zod';

export const CreateEventRequest = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z.enum(['VENUE', 'ONLINE']).optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.'),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.'),
      title: z.string().describe('Event title.').min(1).max(120),
      shortDescription: z
        .string()
        .describe(
          'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .max(500)
        .optional()
        .nullable(),
      detailedDescription: z
        .string()
        .describe(
          'Detailed description of an event.\n\nThis field is deprecated.'
        )
        .max(50000)
        .optional()
        .nullable(),
      mainImage: z
        .string()
        .describe(
          'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the event was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the event was updated.')
        .optional()
        .nullable(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .optional(),
      registration: z
        .object({
          type: z.enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE']).optional(),
          status: z
            .enum([
              'UNKNOWN_REGISTRATION_STATUS',
              'CLOSED_AUTOMATICALLY',
              'CLOSED_MANUALLY',
              'OPEN_RSVP',
              'OPEN_RSVP_WAITLIST_ONLY',
              'OPEN_TICKETS',
              'OPEN_EXTERNAL',
              'SCHEDULED_RSVP',
            ])
            .optional(),
          rsvp: z
            .object({
              responseType: z.enum(['YES_ONLY', 'YES_AND_NO']).optional(),
              limit: z
                .number()
                .int()
                .describe('How many guests can RSVP to an event.')
                .optional()
                .nullable(),
              waitlistEnabled: z
                .boolean()
                .describe(
                  'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                )
                .optional(),
              startDate: z
                .date()
                .describe('Registration start date.')
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe('Registration end date.')
                .optional()
                .nullable(),
            })
            .describe('RSVP registration details.')
            .optional(),
          tickets: z
            .object({
              guestsAssignedSeparately: z
                .boolean()
                .describe(
                  'Whether the registration form must be filled out separately for each ticket.'
                )
                .optional(),
              ticketLimitPerOrder: z
                .number()
                .int()
                .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
                .min(0)
                .max(50)
                .optional(),
              currency: z
                .string()
                .describe('Ticket price currency.')
                .optional()
                .nullable(),
              lowestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Lowest ticket price.')
                .optional(),
              highestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Highest ticket price.')
                .optional(),
              soldOut: z
                .boolean()
                .describe('Whether all tickets are sold for the event.')
                .optional()
                .nullable(),
              taxSettings: z
                .object({
                  type: z
                    .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                    .optional(),
                  name: z
                    .string()
                    .describe('Tax name.')
                    .min(1)
                    .max(10)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe('Tax rate. For example, `21.55`.')
                    .optional()
                    .nullable(),
                  appliedToDonations: z
                    .boolean()
                    .describe('Apply tax to donations.')
                    .optional()
                    .nullable(),
                })
                .describe('How tax is applied.')
                .optional(),
              reservationDurationInMinutes: z
                .number()
                .int()
                .describe('Reservation duration in minutes.')
                .min(5)
                .max(30)
                .optional()
                .nullable(),
            })
            .describe('Ticket registration details.')
            .optional(),
          external: z
            .object({
              url: z
                .string()
                .describe('External event registration URL.')
                .url()
                .optional()
                .nullable(),
            })
            .describe('External registration details.')
            .optional(),
          allowedGuestTypes: z.enum(['VISITOR_OR_MEMBER', 'MEMBER']).optional(),
          initialType: z.enum(['RSVP', 'TICKETING']),
          registrationPaused: z
            .boolean()
            .describe('Whether the registration is paused.')
            .optional(),
          registrationDisabled: z
            .boolean()
            .describe('Whether the registration is disabled.')
            .optional(),
        })
        .describe(
          'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
        )
        .optional(),
      calendarUrls: z
        .object({
          google: z
            .string()
            .describe('"Add to Google Calendar" URL.')
            .optional(),
          ics: z
            .string()
            .describe('"Download ICS calendar file" URL.')
            .optional(),
        })
        .describe(
          'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      eventPageUrl: z
        .string()
        .describe(
          'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
        )
        .optional(),
      form: z
        .object({
          controls: z
            .array(
              z.object({
                type: z
                  .enum([
                    'INPUT',
                    'TEXTAREA',
                    'DROPDOWN',
                    'RADIO',
                    'CHECKBOX',
                    'NAME',
                    'GUEST_CONTROL',
                    'ADDRESS_SHORT',
                    'ADDRESS_FULL',
                    'DATE',
                  ])
                  .optional(),
                system: z
                  .boolean()
                  .describe(
                    'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Deprecated: Use `id` or `_id`.')
                  .optional(),
                inputs: z
                  .array(
                    z.object({
                      name: z.string().describe('Field name.').optional(),
                      array: z
                        .boolean()
                        .describe(
                          '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                        )
                        .optional(),
                      label: z
                        .string()
                        .describe('Main field label.')
                        .optional(),
                      additionalLabels: z
                        .record(z.string(), z.string())
                        .describe(
                          'Additional labels for multi-valued fields such as address.'
                        )
                        .optional(),
                      options: z.array(z.string()).optional(),
                      mandatory: z
                        .boolean()
                        .describe('Whether field is mandatory.')
                        .optional(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of accepted characters (relevant for text fields).'
                        )
                        .optional(),
                      type: z
                        .enum([
                          'TEXT',
                          'NUMBER',
                          'TEXT_ARRAY',
                          'DATE_TIME',
                          'ADDRESS',
                        ])
                        .optional(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                        )
                        .optional()
                        .nullable(),
                      defaultOptionSelection: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z.never().optional(),
                            }),
                            z.object({
                              placeholderText: z.never().optional(),
                              optionIndex: z
                                .number()
                                .int()
                                .describe(
                                  '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                )
                                .min(0)
                                .max(199),
                            }),
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z
                                .string()
                                .describe(
                                  'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe(
                          'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                        )
                        .optional(),
                      labels: z
                        .array(
                          z.object({
                            name: z.string().describe('Field name.').optional(),
                            label: z
                              .string()
                              .describe('Field label.')
                              .optional(),
                          })
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                label: z
                  .string()
                  .describe('*Deprecated:** Use `controls.inputs.label`.')
                  .optional(),
                orderIndex: z
                  .number()
                  .int()
                  .describe(
                    'Field controls are sorted by this value in ascending order.'
                  )
                  .optional(),
                _id: z.string().describe('Unique control ID.').optional(),
                deleted: z
                  .boolean()
                  .describe('Whether the input control is deleted.')
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
          messages: z
            .object({
              rsvp: z
                .object({
                  rsvpYesOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"YES"`.'
                    )
                    .optional(),
                  rsvpNoOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"NO"`.'
                    )
                    .optional(),
                  positiveMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                    )
                    .optional(),
                  waitlistMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                    )
                    .optional(),
                  negativeMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for negative response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                    )
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('"Submit form" call-to-action label text.')
                    .optional(),
                })
                .describe(
                  '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                )
                .optional(),
              checkout: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for response.')
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('Submit form call-to-action label text.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      downloadTicketsLabel: z
                        .string()
                        .describe(
                          '"Download tickets" call-to-action label text.'
                        )
                        .optional(),
                      addToCalendarLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareEventLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after checkout.')
                    .optional(),
                })
                .describe('Checkout form messages.')
                .optional(),
              registrationClosed: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event registration is closed.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event registration is closed.')
                .optional(),
              ticketsUnavailable: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event tickets are unavailable.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event tickets are unavailable.')
                .optional(),
            })
            .describe(
              'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
            )
            .optional(),
        })
        .describe(
          'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
        )
        .optional(),
      summaries: z
        .object({
          rsvps: z
            .object({
              totalCount: z
                .number()
                .int()
                .describe('Total number of RSVPs.')
                .optional(),
              yesCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `YES`.')
                .optional(),
              noCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `NO`.')
                .optional(),
              waitlistCount: z
                .number()
                .int()
                .describe('Number of RSVPs in a waitlist.')
                .optional(),
            })
            .describe('RSVP summary of guests.')
            .optional(),
          tickets: z
            .object({
              ticketsSold: z
                .number()
                .int()
                .describe('Number of sold tickets.')
                .optional(),
              revenue: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Total revenue. Taxes and payment provider fees aren't deducted."
                )
                .optional(),
              currencyLocked: z
                .boolean()
                .describe(
                  "Whether the currency is locked and can't be changed."
                )
                .optional(),
              totalOrders: z
                .number()
                .int()
                .describe('Number of orders placed.')
                .optional(),
              totalSales: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Total balance of confirmed transactions.')
                .optional(),
            })
            .describe(
              "Summary of revenue and sold tickets. Archived orders aren't included."
            )
            .optional(),
        })
        .describe(
          'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
        )
        .optional(),
      instanceId: z
        .string()
        .describe('Instance ID of the site where the event is hosted.')
        .optional(),
      guestListSettings: z
        .object({
          displayedPublicly: z
            .boolean()
            .describe(
              'Whether the guest list is displayed publicly to all guests.'
            )
            .optional(),
        })
        .describe('Guest list configuration.')
        .optional(),
      userId: z
        .string()
        .describe('ID of the user who created the event.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      onlineConferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
            )
            .optional(),
          providerId: z
            .string()
            .describe('Conference host ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z.enum(['MEETING', 'WEBINAR']).optional(),
          session: z
            .object({
              hostLink: z
                .string()
                .describe(
                  'Link for the event host to start the online conference session.'
                )
                .optional(),
              guestLink: z
                .string()
                .describe(
                  'Link for guests to join the online conference session.'
                )
                .optional(),
              password: z
                .string()
                .describe(
                  'Password required to join the online conferencing session (when relevant).'
                )
                .optional()
                .nullable(),
              sessionCreated: z
                .boolean()
                .describe(
                  'Whether the session was created successfully on the event host side.'
                )
                .optional()
                .nullable(),
              sessionId: z
                .string()
                .describe('Unique session ID.')
                .optional()
                .nullable(),
            })
            .describe('Online conferencing session information.')
            .optional(),
        })
        .describe(
          'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
        )
        .optional(),
      seoSettings: z
        .object({
          slug: z.string().describe('URL slug.').max(130).optional(),
          advancedSeoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Advanced SEO data.')
            .optional(),
          hidden: z
            .boolean()
            .describe('Whether the slug is hidden from the SEO sitemap.')
            .optional()
            .nullable(),
        })
        .describe(
          'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
        )
        .optional(),
      contactLabel: z
        .string()
        .describe('Assigned contacts label key.')
        .optional()
        .nullable(),
      agendaSettings: z
        .object({
          enabled: z
            .boolean()
            .describe('Whether the schedule is enabled for the event.')
            .optional(),
          pageUrl: z.string().describe('Schedule page URL.').optional(),
        })
        .describe(
          'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
        )
        .optional(),
      eventDisplaySettings: z
        .object({
          hideEventDetailsButton: z
            .boolean()
            .describe(
              'Whether event details button is hidden. Only available for events with no registration.'
            )
            .optional()
            .nullable(),
          hideEventDetailsPage: z
            .boolean()
            .describe(
              'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
            )
            .optional()
            .nullable(),
        })
        .describe('Visual settings for event.')
        .optional(),
      description: z
        .any()
        .describe(
          'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
        )
        .optional(),
      publishedDate: z
        .date()
        .describe('Event publish timestamp.')
        .optional()
        .nullable(),
    })
    .describe('Event data.'),
  options: z
    .object({
      draft: z
        .boolean()
        .describe('Whether to create the event as a draft.')
        .optional(),
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CreateEventResponse = z.object({
  _id: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  location: z
    .object({
      name: z
        .string()
        .describe(
          'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
        )
        .max(50)
        .optional()
        .nullable(),
      type: z.enum(['VENUE', 'ONLINE']).describe('Location type.').optional(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Exact location address.')
        .optional(),
      locationTbd: z
        .boolean()
        .describe('Whether the event location is TBD.')
        .optional()
        .nullable(),
    })
    .describe('Event location.')
    .optional(),
  dateAndTimeSettings: z
    .object({
      dateAndTimeTbd: z
        .boolean()
        .describe('Whether the event date and time are TBD.')
        .optional()
        .nullable(),
      dateAndTimeTbdMessage: z
        .string()
        .describe(
          'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
        )
        .max(100)
        .optional()
        .nullable(),
      startDate: z
        .date()
        .describe(
          'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .optional()
        .nullable(),
      timeZoneId: z
        .string()
        .describe(
          'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .max(100)
        .optional()
        .nullable(),
      hideEndDate: z
        .boolean()
        .describe(
          'Whether the end date is hidden in the formatted date and time.'
        )
        .optional()
        .nullable(),
      showTimeZone: z
        .boolean()
        .describe(
          'Whether the time zone is displayed in the formatted schedule.'
        )
        .optional()
        .nullable(),
      recurrenceStatus: z
        .enum([
          'ONE_TIME',
          'RECURRING',
          'RECURRING_UPCOMING',
          'RECURRING_RECENTLY_ENDED',
          'RECURRING_RECENTLY_CANCELED',
        ])
        .describe('Repeating event status.')
        .optional(),
      recurringEvents: z
        .object({
          individualEventDates: z
            .array(
              z.object({
                startDate: z
                  .date()
                  .describe('Event start date.')
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe('Event end date.')
                  .optional()
                  .nullable(),
                timeZoneId: z
                  .string()
                  .describe(
                    'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                showTimeZone: z
                  .boolean()
                  .describe(
                    'Whether the time zone is displayed in a formatted schedule.'
                  )
                  .optional(),
              })
            )
            .max(1000)
            .optional(),
          categoryId: z
            .string()
            .describe(
              'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Event repetitions.')
        .optional(),
      formatted: z
        .object({
          dateAndTime: z
            .string()
            .describe(
              'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
            )
            .max(500)
            .optional()
            .nullable(),
          startDate: z
            .string()
            .describe(
              'Formatted start date of the event. Empty for TBD schedules.'
            )
            .max(500)
            .optional()
            .nullable(),
          startTime: z
            .string()
            .describe(
              'Formatted start time of the event. Empty for TBD schedules.'
            )
            .max(500)
            .optional()
            .nullable(),
          endDate: z
            .string()
            .describe(
              'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
            )
            .max(500)
            .optional()
            .nullable(),
          endTime: z
            .string()
            .describe(
              'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
            )
            .max(500)
            .optional()
            .nullable(),
        })
        .describe('Formatted date and time settings.')
        .optional(),
    })
    .describe('Event date and time settings.')
    .optional(),
  title: z
    .string()
    .describe('Event title.')
    .min(1)
    .max(120)
    .optional()
    .nullable(),
  shortDescription: z
    .string()
    .describe(
      'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .max(500)
    .optional()
    .nullable(),
  detailedDescription: z
    .string()
    .describe('Detailed description of an event.\n\nThis field is deprecated.')
    .max(50000)
    .optional()
    .nullable(),
  mainImage: z
    .string()
    .describe(
      'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .optional(),
  slug: z
    .string()
    .describe(
      'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time when the event was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time when the event was updated.')
    .optional()
    .nullable(),
  status: z
    .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
    .describe('Event status.')
    .optional(),
  registration: z
    .object({
      type: z
        .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
        .describe('Reflects the current state of the registration.')
        .optional(),
      status: z
        .enum([
          'UNKNOWN_REGISTRATION_STATUS',
          'CLOSED_AUTOMATICALLY',
          'CLOSED_MANUALLY',
          'OPEN_RSVP',
          'OPEN_RSVP_WAITLIST_ONLY',
          'OPEN_TICKETS',
          'OPEN_EXTERNAL',
          'SCHEDULED_RSVP',
        ])
        .describe('Registration status.')
        .optional(),
      rsvp: z
        .object({
          responseType: z
            .enum(['YES_ONLY', 'YES_AND_NO'])
            .describe('Available answers for registration to an event.')
            .optional(),
          limit: z
            .number()
            .int()
            .describe('How many guests can RSVP to an event.')
            .optional()
            .nullable(),
          waitlistEnabled: z
            .boolean()
            .describe(
              'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
            )
            .optional(),
          startDate: z
            .date()
            .describe('Registration start date.')
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe('Registration end date.')
            .optional()
            .nullable(),
        })
        .describe('RSVP registration details.')
        .optional(),
      tickets: z
        .object({
          guestsAssignedSeparately: z
            .boolean()
            .describe(
              'Whether the registration form must be filled out separately for each ticket.'
            )
            .optional(),
          ticketLimitPerOrder: z
            .number()
            .int()
            .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
            .min(0)
            .max(50)
            .optional(),
          currency: z
            .string()
            .describe('Ticket price currency.')
            .optional()
            .nullable(),
          lowestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                )
                .optional()
                .nullable(),
            })
            .describe('Lowest ticket price.')
            .optional(),
          highestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                )
                .optional()
                .nullable(),
            })
            .describe('Highest ticket price.')
            .optional(),
          soldOut: z
            .boolean()
            .describe('Whether all tickets are sold for the event.')
            .optional()
            .nullable(),
          taxSettings: z
            .object({
              type: z
                .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                .describe('Tax application settings.')
                .optional(),
              name: z
                .string()
                .describe('Tax name.')
                .min(1)
                .max(10)
                .optional()
                .nullable(),
              rate: z
                .string()
                .describe('Tax rate. For example, `21.55`.')
                .optional()
                .nullable(),
              appliedToDonations: z
                .boolean()
                .describe('Apply tax to donations.')
                .optional()
                .nullable(),
            })
            .describe('How tax is applied.')
            .optional(),
          reservationDurationInMinutes: z
            .number()
            .int()
            .describe('Reservation duration in minutes.')
            .min(5)
            .max(30)
            .optional()
            .nullable(),
        })
        .describe('Ticket registration details.')
        .optional(),
      external: z
        .object({
          url: z
            .string()
            .describe('External event registration URL.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('External registration details.')
        .optional(),
      allowedGuestTypes: z
        .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
        .describe('Types of guests allowed to register.')
        .optional(),
      initialType: z
        .enum(['RSVP', 'TICKETING'])
        .describe(
          "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
        )
        .optional(),
      registrationPaused: z
        .boolean()
        .describe('Whether the registration is paused.')
        .optional(),
      registrationDisabled: z
        .boolean()
        .describe('Whether the registration is disabled.')
        .optional(),
    })
    .describe(
      'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
    )
    .optional(),
  calendarUrls: z
    .object({
      google: z.string().describe('"Add to Google Calendar" URL.').optional(),
      ics: z.string().describe('"Download ICS calendar file" URL.').optional(),
    })
    .describe(
      'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .optional(),
  eventPageUrl: z
    .string()
    .describe(
      'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
    )
    .optional(),
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe(
      'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
    )
    .optional(),
  summaries: z
    .object({
      rsvps: z
        .object({
          totalCount: z
            .number()
            .int()
            .describe('Total number of RSVPs.')
            .optional(),
          yesCount: z
            .number()
            .int()
            .describe('Number of RSVPs with status `YES`.')
            .optional(),
          noCount: z
            .number()
            .int()
            .describe('Number of RSVPs with status `NO`.')
            .optional(),
          waitlistCount: z
            .number()
            .int()
            .describe('Number of RSVPs in a waitlist.')
            .optional(),
        })
        .describe('RSVP summary of guests.')
        .optional(),
      tickets: z
        .object({
          ticketsSold: z
            .number()
            .int()
            .describe('Number of sold tickets.')
            .optional(),
          revenue: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Total revenue. Taxes and payment provider fees aren't deducted."
            )
            .optional(),
          currencyLocked: z
            .boolean()
            .describe("Whether the currency is locked and can't be changed.")
            .optional(),
          totalOrders: z
            .number()
            .int()
            .describe('Number of orders placed.')
            .optional(),
          totalSales: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe('Total balance of confirmed transactions.')
            .optional(),
        })
        .describe(
          "Summary of revenue and sold tickets. Archived orders aren't included."
        )
        .optional(),
    })
    .describe(
      'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
    )
    .optional(),
  instanceId: z
    .string()
    .describe('Instance ID of the site where the event is hosted.')
    .optional(),
  guestListSettings: z
    .object({
      displayedPublicly: z
        .boolean()
        .describe('Whether the guest list is displayed publicly to all guests.')
        .optional(),
    })
    .describe('Guest list configuration.')
    .optional(),
  userId: z
    .string()
    .describe('ID of the user who created the event.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  onlineConferencing: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
        )
        .optional(),
      providerId: z
        .string()
        .describe('Conference host ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['MEETING', 'WEBINAR'])
        .describe('Conference type.')
        .optional(),
      session: z
        .object({
          hostLink: z
            .string()
            .describe(
              'Link for the event host to start the online conference session.'
            )
            .optional(),
          guestLink: z
            .string()
            .describe('Link for guests to join the online conference session.')
            .optional(),
          password: z
            .string()
            .describe(
              'Password required to join the online conferencing session (when relevant).'
            )
            .optional()
            .nullable(),
          sessionCreated: z
            .boolean()
            .describe(
              'Whether the session was created successfully on the event host side.'
            )
            .optional()
            .nullable(),
          sessionId: z
            .string()
            .describe('Unique session ID.')
            .optional()
            .nullable(),
        })
        .describe('Online conferencing session information.')
        .optional(),
    })
    .describe(
      'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
    )
    .optional(),
  seoSettings: z
    .object({
      slug: z.string().describe('URL slug.').max(130).optional(),
      advancedSeoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Advanced SEO data.')
        .optional(),
      hidden: z
        .boolean()
        .describe('Whether the slug is hidden from the SEO sitemap.')
        .optional()
        .nullable(),
    })
    .describe(
      'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
    )
    .optional(),
  contactLabel: z
    .string()
    .describe('Assigned contacts label key.')
    .optional()
    .nullable(),
  agendaSettings: z
    .object({
      enabled: z
        .boolean()
        .describe('Whether the schedule is enabled for the event.')
        .optional(),
      pageUrl: z.string().describe('Schedule page URL.').optional(),
    })
    .describe(
      'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
    )
    .optional(),
  eventDisplaySettings: z
    .object({
      hideEventDetailsButton: z
        .boolean()
        .describe(
          'Whether event details button is hidden. Only available for events with no registration.'
        )
        .optional()
        .nullable(),
      hideEventDetailsPage: z
        .boolean()
        .describe(
          'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Visual settings for event.')
    .optional(),
  description: z
    .any()
    .describe(
      'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
    )
    .optional(),
  publishedDate: z
    .date()
    .describe('Event publish timestamp.')
    .optional()
    .nullable(),
});
export const CloneEventRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      event: z
        .object({
          _id: z
            .string()
            .describe('Event ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          location: z
            .object({
              name: z
                .string()
                .describe(
                  'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
                )
                .max(50)
                .optional()
                .nullable(),
              type: z.enum(['VENUE', 'ONLINE']).optional(),
              address: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                  addressLine2: z.string().optional().nullable(),
                })
                .describe('Exact location address.')
                .optional(),
              locationTbd: z
                .boolean()
                .describe('Whether the event location is TBD.')
                .optional()
                .nullable(),
            })
            .describe('Event location.')
            .optional(),
          dateAndTimeSettings: z
            .object({
              dateAndTimeTbd: z
                .boolean()
                .describe('Whether the event date and time are TBD.')
                .optional()
                .nullable(),
              dateAndTimeTbdMessage: z
                .string()
                .describe(
                  'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
                )
                .max(100)
                .optional()
                .nullable(),
              startDate: z
                .date()
                .describe(
                  'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              timeZoneId: z
                .string()
                .describe(
                  'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .max(100)
                .optional()
                .nullable(),
              hideEndDate: z
                .boolean()
                .describe(
                  'Whether the end date is hidden in the formatted date and time.'
                )
                .optional()
                .nullable(),
              showTimeZone: z
                .boolean()
                .describe(
                  'Whether the time zone is displayed in the formatted schedule.'
                )
                .optional()
                .nullable(),
              recurrenceStatus: z
                .enum([
                  'ONE_TIME',
                  'RECURRING',
                  'RECURRING_UPCOMING',
                  'RECURRING_RECENTLY_ENDED',
                  'RECURRING_RECENTLY_CANCELED',
                ])
                .optional(),
              recurringEvents: z
                .object({
                  individualEventDates: z
                    .array(
                      z.object({
                        startDate: z
                          .date()
                          .describe('Event start date.')
                          .optional()
                          .nullable(),
                        endDate: z
                          .date()
                          .describe('Event end date.')
                          .optional()
                          .nullable(),
                        timeZoneId: z
                          .string()
                          .describe(
                            'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                        showTimeZone: z
                          .boolean()
                          .describe(
                            'Whether the time zone is displayed in a formatted schedule.'
                          )
                          .optional(),
                      })
                    )
                    .max(1000)
                    .optional(),
                  categoryId: z
                    .string()
                    .describe(
                      'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Event repetitions.')
                .optional(),
              formatted: z
                .object({
                  dateAndTime: z
                    .string()
                    .describe(
                      'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startDate: z
                    .string()
                    .describe(
                      'Formatted start date of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startTime: z
                    .string()
                    .describe(
                      'Formatted start time of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endDate: z
                    .string()
                    .describe(
                      'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endTime: z
                    .string()
                    .describe(
                      'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                })
                .describe('Formatted date and time settings.')
                .optional(),
            })
            .describe('Event date and time settings.')
            .optional(),
          title: z
            .string()
            .describe('Event title.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe(
              'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
            )
            .max(500)
            .optional()
            .nullable(),
          detailedDescription: z
            .string()
            .describe(
              'Detailed description of an event.\n\nThis field is deprecated.'
            )
            .max(50000)
            .optional()
            .nullable(),
          mainImage: z
            .string()
            .describe(
              'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
            )
            .optional(),
          slug: z
            .string()
            .describe(
              'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time when the event was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time when the event was updated.')
            .optional()
            .nullable(),
          status: z
            .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
            .optional(),
          registration: z
            .object({
              type: z
                .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
                .optional(),
              status: z
                .enum([
                  'UNKNOWN_REGISTRATION_STATUS',
                  'CLOSED_AUTOMATICALLY',
                  'CLOSED_MANUALLY',
                  'OPEN_RSVP',
                  'OPEN_RSVP_WAITLIST_ONLY',
                  'OPEN_TICKETS',
                  'OPEN_EXTERNAL',
                  'SCHEDULED_RSVP',
                ])
                .optional(),
              rsvp: z
                .object({
                  responseType: z.enum(['YES_ONLY', 'YES_AND_NO']).optional(),
                  limit: z
                    .number()
                    .int()
                    .describe('How many guests can RSVP to an event.')
                    .optional()
                    .nullable(),
                  waitlistEnabled: z
                    .boolean()
                    .describe(
                      'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe('Registration start date.')
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe('Registration end date.')
                    .optional()
                    .nullable(),
                })
                .describe('RSVP registration details.')
                .optional(),
              tickets: z
                .object({
                  guestsAssignedSeparately: z
                    .boolean()
                    .describe(
                      'Whether the registration form must be filled out separately for each ticket.'
                    )
                    .optional(),
                  ticketLimitPerOrder: z
                    .number()
                    .int()
                    .describe(
                      'Ticket limit per order. <br>\nDefault: 20 tickets.'
                    )
                    .min(0)
                    .max(50)
                    .optional(),
                  currency: z
                    .string()
                    .describe('Ticket price currency.')
                    .optional()
                    .nullable(),
                  lowestPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Lowest ticket price.')
                    .optional(),
                  highestPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Highest ticket price.')
                    .optional(),
                  soldOut: z
                    .boolean()
                    .describe('Whether all tickets are sold for the event.')
                    .optional()
                    .nullable(),
                  taxSettings: z
                    .object({
                      type: z
                        .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                        .optional(),
                      name: z
                        .string()
                        .describe('Tax name.')
                        .min(1)
                        .max(10)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe('Tax rate. For example, `21.55`.')
                        .optional()
                        .nullable(),
                      appliedToDonations: z
                        .boolean()
                        .describe('Apply tax to donations.')
                        .optional()
                        .nullable(),
                    })
                    .describe('How tax is applied.')
                    .optional(),
                  reservationDurationInMinutes: z
                    .number()
                    .int()
                    .describe('Reservation duration in minutes.')
                    .min(5)
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe('Ticket registration details.')
                .optional(),
              external: z
                .object({
                  url: z
                    .string()
                    .describe('External event registration URL.')
                    .url()
                    .optional()
                    .nullable(),
                })
                .describe('External registration details.')
                .optional(),
              allowedGuestTypes: z
                .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
                .optional(),
              initialType: z.enum(['RSVP', 'TICKETING']).optional(),
              registrationPaused: z
                .boolean()
                .describe('Whether the registration is paused.')
                .optional(),
              registrationDisabled: z
                .boolean()
                .describe('Whether the registration is disabled.')
                .optional(),
            })
            .describe(
              'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
            )
            .optional(),
          calendarUrls: z
            .object({
              google: z
                .string()
                .describe('"Add to Google Calendar" URL.')
                .optional(),
              ics: z
                .string()
                .describe('"Download ICS calendar file" URL.')
                .optional(),
            })
            .describe(
              'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
            )
            .optional(),
          eventPageUrl: z
            .string()
            .describe(
              'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
            )
            .optional(),
          form: z
            .object({
              controls: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'INPUT',
                        'TEXTAREA',
                        'DROPDOWN',
                        'RADIO',
                        'CHECKBOX',
                        'NAME',
                        'GUEST_CONTROL',
                        'ADDRESS_SHORT',
                        'ADDRESS_FULL',
                        'DATE',
                      ])
                      .optional(),
                    system: z
                      .boolean()
                      .describe(
                        'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Deprecated: Use `id` or `_id`.')
                      .optional(),
                    inputs: z
                      .array(
                        z.object({
                          name: z.string().describe('Field name.').optional(),
                          array: z
                            .boolean()
                            .describe(
                              '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                            )
                            .optional(),
                          label: z
                            .string()
                            .describe('Main field label.')
                            .optional(),
                          additionalLabels: z
                            .record(z.string(), z.string())
                            .describe(
                              'Additional labels for multi-valued fields such as address.'
                            )
                            .optional(),
                          options: z.array(z.string()).optional(),
                          mandatory: z
                            .boolean()
                            .describe('Whether field is mandatory.')
                            .optional(),
                          maxLength: z
                            .number()
                            .int()
                            .describe(
                              'Maximum number of accepted characters (relevant for text fields).'
                            )
                            .optional(),
                          type: z
                            .enum([
                              'TEXT',
                              'NUMBER',
                              'TEXT_ARRAY',
                              'DATE_TIME',
                              'ADDRESS',
                            ])
                            .optional(),
                          maxSize: z
                            .number()
                            .int()
                            .describe(
                              'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                            )
                            .optional()
                            .nullable(),
                          defaultOptionSelection: z
                            .intersection(
                              z.object({}),
                              z.xor([
                                z.object({
                                  optionIndex: z.never().optional(),
                                  placeholderText: z.never().optional(),
                                }),
                                z.object({
                                  placeholderText: z.never().optional(),
                                  optionIndex: z
                                    .number()
                                    .int()
                                    .describe(
                                      '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                    )
                                    .min(0)
                                    .max(199),
                                }),
                                z.object({
                                  optionIndex: z.never().optional(),
                                  placeholderText: z
                                    .string()
                                    .describe(
                                      'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                    )
                                    .max(200),
                                }),
                              ])
                            )
                            .describe(
                              'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                            )
                            .optional(),
                          labels: z
                            .array(
                              z.object({
                                name: z
                                  .string()
                                  .describe('Field name.')
                                  .optional(),
                                label: z
                                  .string()
                                  .describe('Field label.')
                                  .optional(),
                              })
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    label: z
                      .string()
                      .describe('*Deprecated:** Use `controls.inputs.label`.')
                      .optional(),
                    orderIndex: z
                      .number()
                      .int()
                      .describe(
                        'Field controls are sorted by this value in ascending order.'
                      )
                      .optional(),
                    _id: z.string().describe('Unique control ID.').optional(),
                    deleted: z
                      .boolean()
                      .describe('Whether the input control is deleted.')
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
              messages: z
                .object({
                  rsvp: z
                    .object({
                      rsvpYesOption: z
                        .string()
                        .describe(
                          'Label text indicating RSVP\'s `status` is `"YES"`.'
                        )
                        .optional(),
                      rsvpNoOption: z
                        .string()
                        .describe(
                          'Label text indicating RSVP\'s `status` is `"NO"`.'
                        )
                        .optional(),
                      positiveMessages: z
                        .object({
                          title: z
                            .string()
                            .describe('Main form title for positive response.')
                            .optional(),
                          confirmation: z
                            .object({
                              title: z
                                .string()
                                .describe('Confirmation message title.')
                                .optional(),
                              message: z
                                .string()
                                .describe('Confirmation message text.')
                                .optional(),
                              addToCalendarActionLabel: z
                                .string()
                                .describe(
                                  '"Add to calendar" call-to-action label text.'
                                )
                                .optional(),
                              shareActionLabel: z
                                .string()
                                .describe(
                                  '"Share event" call-to-action label text.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Confirmation messages shown after registration.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                        )
                        .optional(),
                      waitlistMessages: z
                        .object({
                          title: z
                            .string()
                            .describe('Main form title for positive response.')
                            .optional(),
                          confirmation: z
                            .object({
                              title: z
                                .string()
                                .describe('Confirmation message title.')
                                .optional(),
                              message: z
                                .string()
                                .describe('Confirmation message text.')
                                .optional(),
                              addToCalendarActionLabel: z
                                .string()
                                .describe(
                                  '"Add to calendar" call-to-action label text.'
                                )
                                .optional(),
                              shareActionLabel: z
                                .string()
                                .describe(
                                  '"Share event" call-to-action label text.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Confirmation messages shown after registration.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                        )
                        .optional(),
                      negativeMessages: z
                        .object({
                          title: z
                            .string()
                            .describe('Main form title for negative response.')
                            .optional(),
                          confirmation: z
                            .object({
                              title: z
                                .string()
                                .describe('Confirmation message title.')
                                .optional(),
                              shareActionLabel: z
                                .string()
                                .describe(
                                  '"Share event" call-to-action label text.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Confirmation messages shown after registration.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                        )
                        .optional(),
                      submitActionLabel: z
                        .string()
                        .describe('"Submit form" call-to-action label text.')
                        .optional(),
                    })
                    .describe(
                      '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                    )
                    .optional(),
                  checkout: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for response.')
                        .optional(),
                      submitActionLabel: z
                        .string()
                        .describe('Submit form call-to-action label text.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          downloadTicketsLabel: z
                            .string()
                            .describe(
                              '"Download tickets" call-to-action label text.'
                            )
                            .optional(),
                          addToCalendarLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareEventLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe('Confirmation messages shown after checkout.')
                        .optional(),
                    })
                    .describe('Checkout form messages.')
                    .optional(),
                  registrationClosed: z
                    .object({
                      message: z
                        .string()
                        .describe(
                          'Message shown when event registration is closed.'
                        )
                        .optional(),
                      exploreEventsActionLabel: z
                        .string()
                        .describe(
                          '"Explore other events" call-to-action label text.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages shown when event registration is closed.'
                    )
                    .optional(),
                  ticketsUnavailable: z
                    .object({
                      message: z
                        .string()
                        .describe(
                          'Message shown when event tickets are unavailable.'
                        )
                        .optional(),
                      exploreEventsActionLabel: z
                        .string()
                        .describe(
                          '"Explore other events" call-to-action label text.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages shown when event tickets are unavailable.'
                    )
                    .optional(),
                })
                .describe(
                  'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
                )
                .optional(),
            })
            .describe(
              'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
            )
            .optional(),
          summaries: z
            .object({
              rsvps: z
                .object({
                  totalCount: z
                    .number()
                    .int()
                    .describe('Total number of RSVPs.')
                    .optional(),
                  yesCount: z
                    .number()
                    .int()
                    .describe('Number of RSVPs with status `YES`.')
                    .optional(),
                  noCount: z
                    .number()
                    .int()
                    .describe('Number of RSVPs with status `NO`.')
                    .optional(),
                  waitlistCount: z
                    .number()
                    .int()
                    .describe('Number of RSVPs in a waitlist.')
                    .optional(),
                })
                .describe('RSVP summary of guests.')
                .optional(),
              tickets: z
                .object({
                  ticketsSold: z
                    .number()
                    .int()
                    .describe('Number of sold tickets.')
                    .optional(),
                  revenue: z
                    .object({
                      amount: z
                        .string()
                        .describe('*Deprecated:** Use `value` instead.')
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Total revenue. Taxes and payment provider fees aren't deducted."
                    )
                    .optional(),
                  currencyLocked: z
                    .boolean()
                    .describe(
                      "Whether the currency is locked and can't be changed."
                    )
                    .optional(),
                  totalOrders: z
                    .number()
                    .int()
                    .describe('Number of orders placed.')
                    .optional(),
                  totalSales: z
                    .object({
                      amount: z
                        .string()
                        .describe('*Deprecated:** Use `value` instead.')
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Total balance of confirmed transactions.')
                    .optional(),
                })
                .describe(
                  "Summary of revenue and sold tickets. Archived orders aren't included."
                )
                .optional(),
            })
            .describe(
              'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
            )
            .optional(),
          instanceId: z
            .string()
            .describe('Instance ID of the site where the event is hosted.')
            .optional(),
          guestListSettings: z
            .object({
              displayedPublicly: z
                .boolean()
                .describe(
                  'Whether the guest list is displayed publicly to all guests.'
                )
                .optional(),
            })
            .describe('Guest list configuration.')
            .optional(),
          userId: z
            .string()
            .describe('ID of the user who created the event.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          onlineConferencing: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
                )
                .optional(),
              providerId: z
                .string()
                .describe('Conference host ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              type: z.enum(['MEETING', 'WEBINAR']).optional(),
              session: z
                .object({
                  hostLink: z
                    .string()
                    .describe(
                      'Link for the event host to start the online conference session.'
                    )
                    .optional(),
                  guestLink: z
                    .string()
                    .describe(
                      'Link for guests to join the online conference session.'
                    )
                    .optional(),
                  password: z
                    .string()
                    .describe(
                      'Password required to join the online conferencing session (when relevant).'
                    )
                    .optional()
                    .nullable(),
                  sessionCreated: z
                    .boolean()
                    .describe(
                      'Whether the session was created successfully on the event host side.'
                    )
                    .optional()
                    .nullable(),
                  sessionId: z
                    .string()
                    .describe('Unique session ID.')
                    .optional()
                    .nullable(),
                })
                .describe('Online conferencing session information.')
                .optional(),
            })
            .describe(
              'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
            )
            .optional(),
          seoSettings: z
            .object({
              slug: z.string().describe('URL slug.').max(130).optional(),
              advancedSeoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Advanced SEO data.')
                .optional(),
              hidden: z
                .boolean()
                .describe('Whether the slug is hidden from the SEO sitemap.')
                .optional()
                .nullable(),
            })
            .describe(
              'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
            )
            .optional(),
          contactLabel: z
            .string()
            .describe('Assigned contacts label key.')
            .optional()
            .nullable(),
          agendaSettings: z
            .object({
              enabled: z
                .boolean()
                .describe('Whether the schedule is enabled for the event.')
                .optional(),
              pageUrl: z.string().describe('Schedule page URL.').optional(),
            })
            .describe(
              'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
            )
            .optional(),
          eventDisplaySettings: z
            .object({
              hideEventDetailsButton: z
                .boolean()
                .describe(
                  'Whether event details button is hidden. Only available for events with no registration.'
                )
                .optional()
                .nullable(),
              hideEventDetailsPage: z
                .boolean()
                .describe(
                  'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
                )
                .optional()
                .nullable(),
            })
            .describe('Visual settings for event.')
            .optional(),
          description: z
            .any()
            .describe(
              'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
            )
            .optional(),
          publishedDate: z
            .date()
            .describe('Event publish timestamp.')
            .optional()
            .nullable(),
        })
        .describe('Event info to change in the cloned event.')
        .optional(),
      draft: z
        .boolean()
        .describe('Whether to clone the event as a draft.')
        .optional(),
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CloneEventResponse = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe(
          'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .max(500)
        .optional()
        .nullable(),
      detailedDescription: z
        .string()
        .describe(
          'Detailed description of an event.\n\nThis field is deprecated.'
        )
        .max(50000)
        .optional()
        .nullable(),
      mainImage: z
        .string()
        .describe(
          'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the event was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the event was updated.')
        .optional()
        .nullable(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
      registration: z
        .object({
          type: z
            .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
            .describe('Reflects the current state of the registration.')
            .optional(),
          status: z
            .enum([
              'UNKNOWN_REGISTRATION_STATUS',
              'CLOSED_AUTOMATICALLY',
              'CLOSED_MANUALLY',
              'OPEN_RSVP',
              'OPEN_RSVP_WAITLIST_ONLY',
              'OPEN_TICKETS',
              'OPEN_EXTERNAL',
              'SCHEDULED_RSVP',
            ])
            .describe('Registration status.')
            .optional(),
          rsvp: z
            .object({
              responseType: z
                .enum(['YES_ONLY', 'YES_AND_NO'])
                .describe('Available answers for registration to an event.')
                .optional(),
              limit: z
                .number()
                .int()
                .describe('How many guests can RSVP to an event.')
                .optional()
                .nullable(),
              waitlistEnabled: z
                .boolean()
                .describe(
                  'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                )
                .optional(),
              startDate: z
                .date()
                .describe('Registration start date.')
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe('Registration end date.')
                .optional()
                .nullable(),
            })
            .describe('RSVP registration details.')
            .optional(),
          tickets: z
            .object({
              guestsAssignedSeparately: z
                .boolean()
                .describe(
                  'Whether the registration form must be filled out separately for each ticket.'
                )
                .optional(),
              ticketLimitPerOrder: z
                .number()
                .int()
                .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
                .min(0)
                .max(50)
                .optional(),
              currency: z
                .string()
                .describe('Ticket price currency.')
                .optional()
                .nullable(),
              lowestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Lowest ticket price.')
                .optional(),
              highestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Highest ticket price.')
                .optional(),
              soldOut: z
                .boolean()
                .describe('Whether all tickets are sold for the event.')
                .optional()
                .nullable(),
              taxSettings: z
                .object({
                  type: z
                    .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                    .describe('Tax application settings.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Tax name.')
                    .min(1)
                    .max(10)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe('Tax rate. For example, `21.55`.')
                    .optional()
                    .nullable(),
                  appliedToDonations: z
                    .boolean()
                    .describe('Apply tax to donations.')
                    .optional()
                    .nullable(),
                })
                .describe('How tax is applied.')
                .optional(),
              reservationDurationInMinutes: z
                .number()
                .int()
                .describe('Reservation duration in minutes.')
                .min(5)
                .max(30)
                .optional()
                .nullable(),
            })
            .describe('Ticket registration details.')
            .optional(),
          external: z
            .object({
              url: z
                .string()
                .describe('External event registration URL.')
                .url()
                .optional()
                .nullable(),
            })
            .describe('External registration details.')
            .optional(),
          allowedGuestTypes: z
            .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
            .describe('Types of guests allowed to register.')
            .optional(),
          initialType: z
            .enum(['RSVP', 'TICKETING'])
            .describe(
              "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
            )
            .optional(),
          registrationPaused: z
            .boolean()
            .describe('Whether the registration is paused.')
            .optional(),
          registrationDisabled: z
            .boolean()
            .describe('Whether the registration is disabled.')
            .optional(),
        })
        .describe(
          'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
        )
        .optional(),
      calendarUrls: z
        .object({
          google: z
            .string()
            .describe('"Add to Google Calendar" URL.')
            .optional(),
          ics: z
            .string()
            .describe('"Download ICS calendar file" URL.')
            .optional(),
        })
        .describe(
          'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      eventPageUrl: z
        .string()
        .describe(
          'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
        )
        .optional(),
      form: z
        .object({
          controls: z
            .array(
              z.object({
                type: z
                  .enum([
                    'INPUT',
                    'TEXTAREA',
                    'DROPDOWN',
                    'RADIO',
                    'CHECKBOX',
                    'NAME',
                    'GUEST_CONTROL',
                    'ADDRESS_SHORT',
                    'ADDRESS_FULL',
                    'DATE',
                  ])
                  .describe('Field control type.')
                  .optional(),
                system: z
                  .boolean()
                  .describe(
                    'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Deprecated: Use `id` or `_id`.')
                  .optional(),
                inputs: z
                  .array(
                    z.object({
                      name: z.string().describe('Field name.').optional(),
                      array: z
                        .boolean()
                        .describe(
                          '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                        )
                        .optional(),
                      label: z
                        .string()
                        .describe('Main field label.')
                        .optional(),
                      additionalLabels: z
                        .record(z.string(), z.string())
                        .describe(
                          'Additional labels for multi-valued fields such as address.'
                        )
                        .optional(),
                      options: z.array(z.string()).optional(),
                      mandatory: z
                        .boolean()
                        .describe('Whether field is mandatory.')
                        .optional(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of accepted characters (relevant for text fields).'
                        )
                        .optional(),
                      type: z
                        .enum([
                          'TEXT',
                          'NUMBER',
                          'TEXT_ARRAY',
                          'DATE_TIME',
                          'ADDRESS',
                        ])
                        .describe(
                          'Type which determines field format.\nUsed to validate submitted response.'
                        )
                        .optional(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                        )
                        .optional()
                        .nullable(),
                      defaultOptionSelection: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z.never().optional(),
                            }),
                            z.object({
                              placeholderText: z.never().optional(),
                              optionIndex: z
                                .number()
                                .int()
                                .describe(
                                  '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                )
                                .min(0)
                                .max(199),
                            }),
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z
                                .string()
                                .describe(
                                  'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe(
                          'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                        )
                        .optional(),
                      labels: z
                        .array(
                          z.object({
                            name: z.string().describe('Field name.').optional(),
                            label: z
                              .string()
                              .describe('Field label.')
                              .optional(),
                          })
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                label: z
                  .string()
                  .describe('*Deprecated:** Use `controls.inputs.label`.')
                  .optional(),
                orderIndex: z
                  .number()
                  .int()
                  .describe(
                    'Field controls are sorted by this value in ascending order.'
                  )
                  .optional(),
                _id: z.string().describe('Unique control ID.').optional(),
                deleted: z
                  .boolean()
                  .describe('Whether the input control is deleted.')
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
          messages: z
            .object({
              rsvp: z
                .object({
                  rsvpYesOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"YES"`.'
                    )
                    .optional(),
                  rsvpNoOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"NO"`.'
                    )
                    .optional(),
                  positiveMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                    )
                    .optional(),
                  waitlistMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                    )
                    .optional(),
                  negativeMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for negative response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                    )
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('"Submit form" call-to-action label text.')
                    .optional(),
                })
                .describe(
                  '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                )
                .optional(),
              checkout: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for response.')
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('Submit form call-to-action label text.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      downloadTicketsLabel: z
                        .string()
                        .describe(
                          '"Download tickets" call-to-action label text.'
                        )
                        .optional(),
                      addToCalendarLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareEventLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after checkout.')
                    .optional(),
                })
                .describe('Checkout form messages.')
                .optional(),
              registrationClosed: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event registration is closed.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event registration is closed.')
                .optional(),
              ticketsUnavailable: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event tickets are unavailable.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event tickets are unavailable.')
                .optional(),
            })
            .describe(
              'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
            )
            .optional(),
        })
        .describe(
          'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
        )
        .optional(),
      summaries: z
        .object({
          rsvps: z
            .object({
              totalCount: z
                .number()
                .int()
                .describe('Total number of RSVPs.')
                .optional(),
              yesCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `YES`.')
                .optional(),
              noCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `NO`.')
                .optional(),
              waitlistCount: z
                .number()
                .int()
                .describe('Number of RSVPs in a waitlist.')
                .optional(),
            })
            .describe('RSVP summary of guests.')
            .optional(),
          tickets: z
            .object({
              ticketsSold: z
                .number()
                .int()
                .describe('Number of sold tickets.')
                .optional(),
              revenue: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Total revenue. Taxes and payment provider fees aren't deducted."
                )
                .optional(),
              currencyLocked: z
                .boolean()
                .describe(
                  "Whether the currency is locked and can't be changed."
                )
                .optional(),
              totalOrders: z
                .number()
                .int()
                .describe('Number of orders placed.')
                .optional(),
              totalSales: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Total balance of confirmed transactions.')
                .optional(),
            })
            .describe(
              "Summary of revenue and sold tickets. Archived orders aren't included."
            )
            .optional(),
        })
        .describe(
          'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
        )
        .optional(),
      instanceId: z
        .string()
        .describe('Instance ID of the site where the event is hosted.')
        .optional(),
      guestListSettings: z
        .object({
          displayedPublicly: z
            .boolean()
            .describe(
              'Whether the guest list is displayed publicly to all guests.'
            )
            .optional(),
        })
        .describe('Guest list configuration.')
        .optional(),
      userId: z
        .string()
        .describe('ID of the user who created the event.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      onlineConferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
            )
            .optional(),
          providerId: z
            .string()
            .describe('Conference host ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['MEETING', 'WEBINAR'])
            .describe('Conference type.')
            .optional(),
          session: z
            .object({
              hostLink: z
                .string()
                .describe(
                  'Link for the event host to start the online conference session.'
                )
                .optional(),
              guestLink: z
                .string()
                .describe(
                  'Link for guests to join the online conference session.'
                )
                .optional(),
              password: z
                .string()
                .describe(
                  'Password required to join the online conferencing session (when relevant).'
                )
                .optional()
                .nullable(),
              sessionCreated: z
                .boolean()
                .describe(
                  'Whether the session was created successfully on the event host side.'
                )
                .optional()
                .nullable(),
              sessionId: z
                .string()
                .describe('Unique session ID.')
                .optional()
                .nullable(),
            })
            .describe('Online conferencing session information.')
            .optional(),
        })
        .describe(
          'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
        )
        .optional(),
      seoSettings: z
        .object({
          slug: z.string().describe('URL slug.').max(130).optional(),
          advancedSeoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Advanced SEO data.')
            .optional(),
          hidden: z
            .boolean()
            .describe('Whether the slug is hidden from the SEO sitemap.')
            .optional()
            .nullable(),
        })
        .describe(
          'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
        )
        .optional(),
      contactLabel: z
        .string()
        .describe('Assigned contacts label key.')
        .optional()
        .nullable(),
      agendaSettings: z
        .object({
          enabled: z
            .boolean()
            .describe('Whether the schedule is enabled for the event.')
            .optional(),
          pageUrl: z.string().describe('Schedule page URL.').optional(),
        })
        .describe(
          'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
        )
        .optional(),
      eventDisplaySettings: z
        .object({
          hideEventDetailsButton: z
            .boolean()
            .describe(
              'Whether event details button is hidden. Only available for events with no registration.'
            )
            .optional()
            .nullable(),
          hideEventDetailsPage: z
            .boolean()
            .describe(
              'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
            )
            .optional()
            .nullable(),
        })
        .describe('Visual settings for event.')
        .optional(),
      description: z
        .any()
        .describe(
          'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
        )
        .optional(),
      publishedDate: z
        .date()
        .describe('Event publish timestamp.')
        .optional()
        .nullable(),
    })
    .describe('Cloned event.')
    .optional(),
});
export const UpdateEventRequest = z.object({
  _id: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      event: z
        .object({
          _id: z
            .string()
            .describe('Event ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          location: z
            .object({
              name: z
                .string()
                .describe(
                  'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
                )
                .max(50)
                .optional()
                .nullable(),
              type: z.enum(['VENUE', 'ONLINE']).optional(),
              address: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                  addressLine2: z.string().optional().nullable(),
                })
                .describe('Exact location address.')
                .optional(),
              locationTbd: z
                .boolean()
                .describe('Whether the event location is TBD.')
                .optional()
                .nullable(),
            })
            .describe('Event location.')
            .optional(),
          dateAndTimeSettings: z
            .object({
              dateAndTimeTbd: z
                .boolean()
                .describe('Whether the event date and time are TBD.')
                .optional()
                .nullable(),
              dateAndTimeTbdMessage: z
                .string()
                .describe(
                  'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
                )
                .max(100)
                .optional()
                .nullable(),
              startDate: z
                .date()
                .describe(
                  'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              timeZoneId: z
                .string()
                .describe(
                  'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .max(100)
                .optional()
                .nullable(),
              hideEndDate: z
                .boolean()
                .describe(
                  'Whether the end date is hidden in the formatted date and time.'
                )
                .optional()
                .nullable(),
              showTimeZone: z
                .boolean()
                .describe(
                  'Whether the time zone is displayed in the formatted schedule.'
                )
                .optional()
                .nullable(),
              recurrenceStatus: z
                .enum([
                  'ONE_TIME',
                  'RECURRING',
                  'RECURRING_UPCOMING',
                  'RECURRING_RECENTLY_ENDED',
                  'RECURRING_RECENTLY_CANCELED',
                ])
                .optional(),
              recurringEvents: z
                .object({
                  individualEventDates: z
                    .array(
                      z.object({
                        startDate: z
                          .date()
                          .describe('Event start date.')
                          .optional()
                          .nullable(),
                        endDate: z
                          .date()
                          .describe('Event end date.')
                          .optional()
                          .nullable(),
                        timeZoneId: z
                          .string()
                          .describe(
                            'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                        showTimeZone: z
                          .boolean()
                          .describe(
                            'Whether the time zone is displayed in a formatted schedule.'
                          )
                          .optional(),
                      })
                    )
                    .max(1000)
                    .optional(),
                  categoryId: z
                    .string()
                    .describe(
                      'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Event repetitions.')
                .optional(),
              formatted: z
                .object({
                  dateAndTime: z
                    .string()
                    .describe(
                      'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startDate: z
                    .string()
                    .describe(
                      'Formatted start date of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startTime: z
                    .string()
                    .describe(
                      'Formatted start time of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endDate: z
                    .string()
                    .describe(
                      'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endTime: z
                    .string()
                    .describe(
                      'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                })
                .describe('Formatted date and time settings.')
                .optional(),
            })
            .describe('Event date and time settings.')
            .optional(),
          title: z
            .string()
            .describe('Event title.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe(
              'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
            )
            .max(500)
            .optional()
            .nullable(),
          detailedDescription: z
            .string()
            .describe(
              'Detailed description of an event.\n\nThis field is deprecated.'
            )
            .max(50000)
            .optional()
            .nullable(),
          mainImage: z
            .string()
            .describe(
              'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
            )
            .optional(),
          slug: z
            .string()
            .describe(
              'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time when the event was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time when the event was updated.')
            .optional()
            .nullable(),
          status: z
            .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
            .optional(),
          registration: z
            .object({
              type: z
                .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
                .optional(),
              status: z
                .enum([
                  'UNKNOWN_REGISTRATION_STATUS',
                  'CLOSED_AUTOMATICALLY',
                  'CLOSED_MANUALLY',
                  'OPEN_RSVP',
                  'OPEN_RSVP_WAITLIST_ONLY',
                  'OPEN_TICKETS',
                  'OPEN_EXTERNAL',
                  'SCHEDULED_RSVP',
                ])
                .optional(),
              rsvp: z
                .object({
                  responseType: z.enum(['YES_ONLY', 'YES_AND_NO']).optional(),
                  limit: z
                    .number()
                    .int()
                    .describe('How many guests can RSVP to an event.')
                    .optional()
                    .nullable(),
                  waitlistEnabled: z
                    .boolean()
                    .describe(
                      'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                    )
                    .optional(),
                  startDate: z
                    .date()
                    .describe('Registration start date.')
                    .optional()
                    .nullable(),
                  endDate: z
                    .date()
                    .describe('Registration end date.')
                    .optional()
                    .nullable(),
                })
                .describe('RSVP registration details.')
                .optional(),
              tickets: z
                .object({
                  guestsAssignedSeparately: z
                    .boolean()
                    .describe(
                      'Whether the registration form must be filled out separately for each ticket.'
                    )
                    .optional(),
                  ticketLimitPerOrder: z
                    .number()
                    .int()
                    .describe(
                      'Ticket limit per order. <br>\nDefault: 20 tickets.'
                    )
                    .min(0)
                    .max(50)
                    .optional(),
                  currency: z
                    .string()
                    .describe('Ticket price currency.')
                    .optional()
                    .nullable(),
                  lowestPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Lowest ticket price.')
                    .optional(),
                  highestPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Highest ticket price.')
                    .optional(),
                  soldOut: z
                    .boolean()
                    .describe('Whether all tickets are sold for the event.')
                    .optional()
                    .nullable(),
                  taxSettings: z
                    .object({
                      type: z
                        .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                        .optional(),
                      name: z
                        .string()
                        .describe('Tax name.')
                        .min(1)
                        .max(10)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe('Tax rate. For example, `21.55`.')
                        .optional()
                        .nullable(),
                      appliedToDonations: z
                        .boolean()
                        .describe('Apply tax to donations.')
                        .optional()
                        .nullable(),
                    })
                    .describe('How tax is applied.')
                    .optional(),
                  reservationDurationInMinutes: z
                    .number()
                    .int()
                    .describe('Reservation duration in minutes.')
                    .min(5)
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe('Ticket registration details.')
                .optional(),
              external: z
                .object({
                  url: z
                    .string()
                    .describe('External event registration URL.')
                    .url()
                    .optional()
                    .nullable(),
                })
                .describe('External registration details.')
                .optional(),
              allowedGuestTypes: z
                .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
                .optional(),
              initialType: z.enum(['RSVP', 'TICKETING']).optional(),
              registrationPaused: z
                .boolean()
                .describe('Whether the registration is paused.')
                .optional(),
              registrationDisabled: z
                .boolean()
                .describe('Whether the registration is disabled.')
                .optional(),
            })
            .describe(
              'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
            )
            .optional(),
          calendarUrls: z
            .object({
              google: z
                .string()
                .describe('"Add to Google Calendar" URL.')
                .optional(),
              ics: z
                .string()
                .describe('"Download ICS calendar file" URL.')
                .optional(),
            })
            .describe(
              'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
            )
            .optional(),
          eventPageUrl: z
            .string()
            .describe(
              'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
            )
            .optional(),
          form: z
            .object({
              controls: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'INPUT',
                        'TEXTAREA',
                        'DROPDOWN',
                        'RADIO',
                        'CHECKBOX',
                        'NAME',
                        'GUEST_CONTROL',
                        'ADDRESS_SHORT',
                        'ADDRESS_FULL',
                        'DATE',
                      ])
                      .optional(),
                    system: z
                      .boolean()
                      .describe(
                        'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Deprecated: Use `id` or `_id`.')
                      .optional(),
                    inputs: z
                      .array(
                        z.object({
                          name: z.string().describe('Field name.').optional(),
                          array: z
                            .boolean()
                            .describe(
                              '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                            )
                            .optional(),
                          label: z
                            .string()
                            .describe('Main field label.')
                            .optional(),
                          additionalLabels: z
                            .record(z.string(), z.string())
                            .describe(
                              'Additional labels for multi-valued fields such as address.'
                            )
                            .optional(),
                          options: z.array(z.string()).optional(),
                          mandatory: z
                            .boolean()
                            .describe('Whether field is mandatory.')
                            .optional(),
                          maxLength: z
                            .number()
                            .int()
                            .describe(
                              'Maximum number of accepted characters (relevant for text fields).'
                            )
                            .optional(),
                          type: z
                            .enum([
                              'TEXT',
                              'NUMBER',
                              'TEXT_ARRAY',
                              'DATE_TIME',
                              'ADDRESS',
                            ])
                            .optional(),
                          maxSize: z
                            .number()
                            .int()
                            .describe(
                              'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                            )
                            .optional()
                            .nullable(),
                          defaultOptionSelection: z
                            .intersection(
                              z.object({}),
                              z.xor([
                                z.object({
                                  optionIndex: z.never().optional(),
                                  placeholderText: z.never().optional(),
                                }),
                                z.object({
                                  placeholderText: z.never().optional(),
                                  optionIndex: z
                                    .number()
                                    .int()
                                    .describe(
                                      '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                    )
                                    .min(0)
                                    .max(199),
                                }),
                                z.object({
                                  optionIndex: z.never().optional(),
                                  placeholderText: z
                                    .string()
                                    .describe(
                                      'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                    )
                                    .max(200),
                                }),
                              ])
                            )
                            .describe(
                              'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                            )
                            .optional(),
                          labels: z
                            .array(
                              z.object({
                                name: z
                                  .string()
                                  .describe('Field name.')
                                  .optional(),
                                label: z
                                  .string()
                                  .describe('Field label.')
                                  .optional(),
                              })
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    label: z
                      .string()
                      .describe('*Deprecated:** Use `controls.inputs.label`.')
                      .optional(),
                    orderIndex: z
                      .number()
                      .int()
                      .describe(
                        'Field controls are sorted by this value in ascending order.'
                      )
                      .optional(),
                    _id: z.string().describe('Unique control ID.').optional(),
                    deleted: z
                      .boolean()
                      .describe('Whether the input control is deleted.')
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
              messages: z
                .object({
                  rsvp: z
                    .object({
                      rsvpYesOption: z
                        .string()
                        .describe(
                          'Label text indicating RSVP\'s `status` is `"YES"`.'
                        )
                        .optional(),
                      rsvpNoOption: z
                        .string()
                        .describe(
                          'Label text indicating RSVP\'s `status` is `"NO"`.'
                        )
                        .optional(),
                      positiveMessages: z
                        .object({
                          title: z
                            .string()
                            .describe('Main form title for positive response.')
                            .optional(),
                          confirmation: z
                            .object({
                              title: z
                                .string()
                                .describe('Confirmation message title.')
                                .optional(),
                              message: z
                                .string()
                                .describe('Confirmation message text.')
                                .optional(),
                              addToCalendarActionLabel: z
                                .string()
                                .describe(
                                  '"Add to calendar" call-to-action label text.'
                                )
                                .optional(),
                              shareActionLabel: z
                                .string()
                                .describe(
                                  '"Share event" call-to-action label text.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Confirmation messages shown after registration.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                        )
                        .optional(),
                      waitlistMessages: z
                        .object({
                          title: z
                            .string()
                            .describe('Main form title for positive response.')
                            .optional(),
                          confirmation: z
                            .object({
                              title: z
                                .string()
                                .describe('Confirmation message title.')
                                .optional(),
                              message: z
                                .string()
                                .describe('Confirmation message text.')
                                .optional(),
                              addToCalendarActionLabel: z
                                .string()
                                .describe(
                                  '"Add to calendar" call-to-action label text.'
                                )
                                .optional(),
                              shareActionLabel: z
                                .string()
                                .describe(
                                  '"Share event" call-to-action label text.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Confirmation messages shown after registration.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                        )
                        .optional(),
                      negativeMessages: z
                        .object({
                          title: z
                            .string()
                            .describe('Main form title for negative response.')
                            .optional(),
                          confirmation: z
                            .object({
                              title: z
                                .string()
                                .describe('Confirmation message title.')
                                .optional(),
                              shareActionLabel: z
                                .string()
                                .describe(
                                  '"Share event" call-to-action label text.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Confirmation messages shown after registration.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                        )
                        .optional(),
                      submitActionLabel: z
                        .string()
                        .describe('"Submit form" call-to-action label text.')
                        .optional(),
                    })
                    .describe(
                      '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                    )
                    .optional(),
                  checkout: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for response.')
                        .optional(),
                      submitActionLabel: z
                        .string()
                        .describe('Submit form call-to-action label text.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          downloadTicketsLabel: z
                            .string()
                            .describe(
                              '"Download tickets" call-to-action label text.'
                            )
                            .optional(),
                          addToCalendarLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareEventLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe('Confirmation messages shown after checkout.')
                        .optional(),
                    })
                    .describe('Checkout form messages.')
                    .optional(),
                  registrationClosed: z
                    .object({
                      message: z
                        .string()
                        .describe(
                          'Message shown when event registration is closed.'
                        )
                        .optional(),
                      exploreEventsActionLabel: z
                        .string()
                        .describe(
                          '"Explore other events" call-to-action label text.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages shown when event registration is closed.'
                    )
                    .optional(),
                  ticketsUnavailable: z
                    .object({
                      message: z
                        .string()
                        .describe(
                          'Message shown when event tickets are unavailable.'
                        )
                        .optional(),
                      exploreEventsActionLabel: z
                        .string()
                        .describe(
                          '"Explore other events" call-to-action label text.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages shown when event tickets are unavailable.'
                    )
                    .optional(),
                })
                .describe(
                  'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
                )
                .optional(),
            })
            .describe(
              'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
            )
            .optional(),
          summaries: z
            .object({
              rsvps: z
                .object({
                  totalCount: z
                    .number()
                    .int()
                    .describe('Total number of RSVPs.')
                    .optional(),
                  yesCount: z
                    .number()
                    .int()
                    .describe('Number of RSVPs with status `YES`.')
                    .optional(),
                  noCount: z
                    .number()
                    .int()
                    .describe('Number of RSVPs with status `NO`.')
                    .optional(),
                  waitlistCount: z
                    .number()
                    .int()
                    .describe('Number of RSVPs in a waitlist.')
                    .optional(),
                })
                .describe('RSVP summary of guests.')
                .optional(),
              tickets: z
                .object({
                  ticketsSold: z
                    .number()
                    .int()
                    .describe('Number of sold tickets.')
                    .optional(),
                  revenue: z
                    .object({
                      amount: z
                        .string()
                        .describe('*Deprecated:** Use `value` instead.')
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Total revenue. Taxes and payment provider fees aren't deducted."
                    )
                    .optional(),
                  currencyLocked: z
                    .boolean()
                    .describe(
                      "Whether the currency is locked and can't be changed."
                    )
                    .optional(),
                  totalOrders: z
                    .number()
                    .int()
                    .describe('Number of orders placed.')
                    .optional(),
                  totalSales: z
                    .object({
                      amount: z
                        .string()
                        .describe('*Deprecated:** Use `value` instead.')
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Total balance of confirmed transactions.')
                    .optional(),
                })
                .describe(
                  "Summary of revenue and sold tickets. Archived orders aren't included."
                )
                .optional(),
            })
            .describe(
              'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
            )
            .optional(),
          instanceId: z
            .string()
            .describe('Instance ID of the site where the event is hosted.')
            .optional(),
          guestListSettings: z
            .object({
              displayedPublicly: z
                .boolean()
                .describe(
                  'Whether the guest list is displayed publicly to all guests.'
                )
                .optional(),
            })
            .describe('Guest list configuration.')
            .optional(),
          userId: z
            .string()
            .describe('ID of the user who created the event.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          onlineConferencing: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
                )
                .optional(),
              providerId: z
                .string()
                .describe('Conference host ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              type: z.enum(['MEETING', 'WEBINAR']).optional(),
              session: z
                .object({
                  hostLink: z
                    .string()
                    .describe(
                      'Link for the event host to start the online conference session.'
                    )
                    .optional(),
                  guestLink: z
                    .string()
                    .describe(
                      'Link for guests to join the online conference session.'
                    )
                    .optional(),
                  password: z
                    .string()
                    .describe(
                      'Password required to join the online conferencing session (when relevant).'
                    )
                    .optional()
                    .nullable(),
                  sessionCreated: z
                    .boolean()
                    .describe(
                      'Whether the session was created successfully on the event host side.'
                    )
                    .optional()
                    .nullable(),
                  sessionId: z
                    .string()
                    .describe('Unique session ID.')
                    .optional()
                    .nullable(),
                })
                .describe('Online conferencing session information.')
                .optional(),
            })
            .describe(
              'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
            )
            .optional(),
          seoSettings: z
            .object({
              slug: z.string().describe('URL slug.').max(130).optional(),
              advancedSeoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Advanced SEO data.')
                .optional(),
              hidden: z
                .boolean()
                .describe('Whether the slug is hidden from the SEO sitemap.')
                .optional()
                .nullable(),
            })
            .describe(
              'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
            )
            .optional(),
          contactLabel: z
            .string()
            .describe('Assigned contacts label key.')
            .optional()
            .nullable(),
          agendaSettings: z
            .object({
              enabled: z
                .boolean()
                .describe('Whether the schedule is enabled for the event.')
                .optional(),
              pageUrl: z.string().describe('Schedule page URL.').optional(),
            })
            .describe(
              'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
            )
            .optional(),
          eventDisplaySettings: z
            .object({
              hideEventDetailsButton: z
                .boolean()
                .describe(
                  'Whether event details button is hidden. Only available for events with no registration.'
                )
                .optional()
                .nullable(),
              hideEventDetailsPage: z
                .boolean()
                .describe(
                  'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
                )
                .optional()
                .nullable(),
            })
            .describe('Visual settings for event.')
            .optional(),
          description: z
            .any()
            .describe(
              'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
            )
            .optional(),
          publishedDate: z
            .date()
            .describe('Event publish timestamp.')
            .optional()
            .nullable(),
        })
        .describe('Event info to update.')
        .optional(),
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const UpdateEventResponse = z.object({
  _id: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  location: z
    .object({
      name: z
        .string()
        .describe(
          'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
        )
        .max(50)
        .optional()
        .nullable(),
      type: z.enum(['VENUE', 'ONLINE']).describe('Location type.').optional(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Exact location address.')
        .optional(),
      locationTbd: z
        .boolean()
        .describe('Whether the event location is TBD.')
        .optional()
        .nullable(),
    })
    .describe('Event location.')
    .optional(),
  dateAndTimeSettings: z
    .object({
      dateAndTimeTbd: z
        .boolean()
        .describe('Whether the event date and time are TBD.')
        .optional()
        .nullable(),
      dateAndTimeTbdMessage: z
        .string()
        .describe(
          'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
        )
        .max(100)
        .optional()
        .nullable(),
      startDate: z
        .date()
        .describe(
          'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .optional()
        .nullable(),
      timeZoneId: z
        .string()
        .describe(
          'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .max(100)
        .optional()
        .nullable(),
      hideEndDate: z
        .boolean()
        .describe(
          'Whether the end date is hidden in the formatted date and time.'
        )
        .optional()
        .nullable(),
      showTimeZone: z
        .boolean()
        .describe(
          'Whether the time zone is displayed in the formatted schedule.'
        )
        .optional()
        .nullable(),
      recurrenceStatus: z
        .enum([
          'ONE_TIME',
          'RECURRING',
          'RECURRING_UPCOMING',
          'RECURRING_RECENTLY_ENDED',
          'RECURRING_RECENTLY_CANCELED',
        ])
        .describe('Repeating event status.')
        .optional(),
      recurringEvents: z
        .object({
          individualEventDates: z
            .array(
              z.object({
                startDate: z
                  .date()
                  .describe('Event start date.')
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe('Event end date.')
                  .optional()
                  .nullable(),
                timeZoneId: z
                  .string()
                  .describe(
                    'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                showTimeZone: z
                  .boolean()
                  .describe(
                    'Whether the time zone is displayed in a formatted schedule.'
                  )
                  .optional(),
              })
            )
            .max(1000)
            .optional(),
          categoryId: z
            .string()
            .describe(
              'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Event repetitions.')
        .optional(),
      formatted: z
        .object({
          dateAndTime: z
            .string()
            .describe(
              'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
            )
            .max(500)
            .optional()
            .nullable(),
          startDate: z
            .string()
            .describe(
              'Formatted start date of the event. Empty for TBD schedules.'
            )
            .max(500)
            .optional()
            .nullable(),
          startTime: z
            .string()
            .describe(
              'Formatted start time of the event. Empty for TBD schedules.'
            )
            .max(500)
            .optional()
            .nullable(),
          endDate: z
            .string()
            .describe(
              'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
            )
            .max(500)
            .optional()
            .nullable(),
          endTime: z
            .string()
            .describe(
              'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
            )
            .max(500)
            .optional()
            .nullable(),
        })
        .describe('Formatted date and time settings.')
        .optional(),
    })
    .describe('Event date and time settings.')
    .optional(),
  title: z
    .string()
    .describe('Event title.')
    .min(1)
    .max(120)
    .optional()
    .nullable(),
  shortDescription: z
    .string()
    .describe(
      'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .max(500)
    .optional()
    .nullable(),
  detailedDescription: z
    .string()
    .describe('Detailed description of an event.\n\nThis field is deprecated.')
    .max(50000)
    .optional()
    .nullable(),
  mainImage: z
    .string()
    .describe(
      'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .optional(),
  slug: z
    .string()
    .describe(
      'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time when the event was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time when the event was updated.')
    .optional()
    .nullable(),
  status: z
    .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
    .describe('Event status.')
    .optional(),
  registration: z
    .object({
      type: z
        .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
        .describe('Reflects the current state of the registration.')
        .optional(),
      status: z
        .enum([
          'UNKNOWN_REGISTRATION_STATUS',
          'CLOSED_AUTOMATICALLY',
          'CLOSED_MANUALLY',
          'OPEN_RSVP',
          'OPEN_RSVP_WAITLIST_ONLY',
          'OPEN_TICKETS',
          'OPEN_EXTERNAL',
          'SCHEDULED_RSVP',
        ])
        .describe('Registration status.')
        .optional(),
      rsvp: z
        .object({
          responseType: z
            .enum(['YES_ONLY', 'YES_AND_NO'])
            .describe('Available answers for registration to an event.')
            .optional(),
          limit: z
            .number()
            .int()
            .describe('How many guests can RSVP to an event.')
            .optional()
            .nullable(),
          waitlistEnabled: z
            .boolean()
            .describe(
              'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
            )
            .optional(),
          startDate: z
            .date()
            .describe('Registration start date.')
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe('Registration end date.')
            .optional()
            .nullable(),
        })
        .describe('RSVP registration details.')
        .optional(),
      tickets: z
        .object({
          guestsAssignedSeparately: z
            .boolean()
            .describe(
              'Whether the registration form must be filled out separately for each ticket.'
            )
            .optional(),
          ticketLimitPerOrder: z
            .number()
            .int()
            .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
            .min(0)
            .max(50)
            .optional(),
          currency: z
            .string()
            .describe('Ticket price currency.')
            .optional()
            .nullable(),
          lowestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                )
                .optional()
                .nullable(),
            })
            .describe('Lowest ticket price.')
            .optional(),
          highestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                )
                .optional()
                .nullable(),
            })
            .describe('Highest ticket price.')
            .optional(),
          soldOut: z
            .boolean()
            .describe('Whether all tickets are sold for the event.')
            .optional()
            .nullable(),
          taxSettings: z
            .object({
              type: z
                .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                .describe('Tax application settings.')
                .optional(),
              name: z
                .string()
                .describe('Tax name.')
                .min(1)
                .max(10)
                .optional()
                .nullable(),
              rate: z
                .string()
                .describe('Tax rate. For example, `21.55`.')
                .optional()
                .nullable(),
              appliedToDonations: z
                .boolean()
                .describe('Apply tax to donations.')
                .optional()
                .nullable(),
            })
            .describe('How tax is applied.')
            .optional(),
          reservationDurationInMinutes: z
            .number()
            .int()
            .describe('Reservation duration in minutes.')
            .min(5)
            .max(30)
            .optional()
            .nullable(),
        })
        .describe('Ticket registration details.')
        .optional(),
      external: z
        .object({
          url: z
            .string()
            .describe('External event registration URL.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('External registration details.')
        .optional(),
      allowedGuestTypes: z
        .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
        .describe('Types of guests allowed to register.')
        .optional(),
      initialType: z
        .enum(['RSVP', 'TICKETING'])
        .describe(
          "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
        )
        .optional(),
      registrationPaused: z
        .boolean()
        .describe('Whether the registration is paused.')
        .optional(),
      registrationDisabled: z
        .boolean()
        .describe('Whether the registration is disabled.')
        .optional(),
    })
    .describe(
      'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
    )
    .optional(),
  calendarUrls: z
    .object({
      google: z.string().describe('"Add to Google Calendar" URL.').optional(),
      ics: z.string().describe('"Download ICS calendar file" URL.').optional(),
    })
    .describe(
      'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .optional(),
  eventPageUrl: z
    .string()
    .describe(
      'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
    )
    .optional(),
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe(
      'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
    )
    .optional(),
  summaries: z
    .object({
      rsvps: z
        .object({
          totalCount: z
            .number()
            .int()
            .describe('Total number of RSVPs.')
            .optional(),
          yesCount: z
            .number()
            .int()
            .describe('Number of RSVPs with status `YES`.')
            .optional(),
          noCount: z
            .number()
            .int()
            .describe('Number of RSVPs with status `NO`.')
            .optional(),
          waitlistCount: z
            .number()
            .int()
            .describe('Number of RSVPs in a waitlist.')
            .optional(),
        })
        .describe('RSVP summary of guests.')
        .optional(),
      tickets: z
        .object({
          ticketsSold: z
            .number()
            .int()
            .describe('Number of sold tickets.')
            .optional(),
          revenue: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Total revenue. Taxes and payment provider fees aren't deducted."
            )
            .optional(),
          currencyLocked: z
            .boolean()
            .describe("Whether the currency is locked and can't be changed.")
            .optional(),
          totalOrders: z
            .number()
            .int()
            .describe('Number of orders placed.')
            .optional(),
          totalSales: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe('Total balance of confirmed transactions.')
            .optional(),
        })
        .describe(
          "Summary of revenue and sold tickets. Archived orders aren't included."
        )
        .optional(),
    })
    .describe(
      'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
    )
    .optional(),
  instanceId: z
    .string()
    .describe('Instance ID of the site where the event is hosted.')
    .optional(),
  guestListSettings: z
    .object({
      displayedPublicly: z
        .boolean()
        .describe('Whether the guest list is displayed publicly to all guests.')
        .optional(),
    })
    .describe('Guest list configuration.')
    .optional(),
  userId: z
    .string()
    .describe('ID of the user who created the event.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  onlineConferencing: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
        )
        .optional(),
      providerId: z
        .string()
        .describe('Conference host ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['MEETING', 'WEBINAR'])
        .describe('Conference type.')
        .optional(),
      session: z
        .object({
          hostLink: z
            .string()
            .describe(
              'Link for the event host to start the online conference session.'
            )
            .optional(),
          guestLink: z
            .string()
            .describe('Link for guests to join the online conference session.')
            .optional(),
          password: z
            .string()
            .describe(
              'Password required to join the online conferencing session (when relevant).'
            )
            .optional()
            .nullable(),
          sessionCreated: z
            .boolean()
            .describe(
              'Whether the session was created successfully on the event host side.'
            )
            .optional()
            .nullable(),
          sessionId: z
            .string()
            .describe('Unique session ID.')
            .optional()
            .nullable(),
        })
        .describe('Online conferencing session information.')
        .optional(),
    })
    .describe(
      'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
    )
    .optional(),
  seoSettings: z
    .object({
      slug: z.string().describe('URL slug.').max(130).optional(),
      advancedSeoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Advanced SEO data.')
        .optional(),
      hidden: z
        .boolean()
        .describe('Whether the slug is hidden from the SEO sitemap.')
        .optional()
        .nullable(),
    })
    .describe(
      'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
    )
    .optional(),
  contactLabel: z
    .string()
    .describe('Assigned contacts label key.')
    .optional()
    .nullable(),
  agendaSettings: z
    .object({
      enabled: z
        .boolean()
        .describe('Whether the schedule is enabled for the event.')
        .optional(),
      pageUrl: z.string().describe('Schedule page URL.').optional(),
    })
    .describe(
      'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
    )
    .optional(),
  eventDisplaySettings: z
    .object({
      hideEventDetailsButton: z
        .boolean()
        .describe(
          'Whether event details button is hidden. Only available for events with no registration.'
        )
        .optional()
        .nullable(),
      hideEventDetailsPage: z
        .boolean()
        .describe(
          'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Visual settings for event.')
    .optional(),
  description: z
    .any()
    .describe(
      'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
    )
    .optional(),
  publishedDate: z
    .date()
    .describe('Event publish timestamp.')
    .optional()
    .nullable(),
});
export const PublishDraftEventRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const PublishDraftEventResponse = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe(
          'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .max(500)
        .optional()
        .nullable(),
      detailedDescription: z
        .string()
        .describe(
          'Detailed description of an event.\n\nThis field is deprecated.'
        )
        .max(50000)
        .optional()
        .nullable(),
      mainImage: z
        .string()
        .describe(
          'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the event was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the event was updated.')
        .optional()
        .nullable(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
      registration: z
        .object({
          type: z
            .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
            .describe('Reflects the current state of the registration.')
            .optional(),
          status: z
            .enum([
              'UNKNOWN_REGISTRATION_STATUS',
              'CLOSED_AUTOMATICALLY',
              'CLOSED_MANUALLY',
              'OPEN_RSVP',
              'OPEN_RSVP_WAITLIST_ONLY',
              'OPEN_TICKETS',
              'OPEN_EXTERNAL',
              'SCHEDULED_RSVP',
            ])
            .describe('Registration status.')
            .optional(),
          rsvp: z
            .object({
              responseType: z
                .enum(['YES_ONLY', 'YES_AND_NO'])
                .describe('Available answers for registration to an event.')
                .optional(),
              limit: z
                .number()
                .int()
                .describe('How many guests can RSVP to an event.')
                .optional()
                .nullable(),
              waitlistEnabled: z
                .boolean()
                .describe(
                  'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                )
                .optional(),
              startDate: z
                .date()
                .describe('Registration start date.')
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe('Registration end date.')
                .optional()
                .nullable(),
            })
            .describe('RSVP registration details.')
            .optional(),
          tickets: z
            .object({
              guestsAssignedSeparately: z
                .boolean()
                .describe(
                  'Whether the registration form must be filled out separately for each ticket.'
                )
                .optional(),
              ticketLimitPerOrder: z
                .number()
                .int()
                .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
                .min(0)
                .max(50)
                .optional(),
              currency: z
                .string()
                .describe('Ticket price currency.')
                .optional()
                .nullable(),
              lowestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Lowest ticket price.')
                .optional(),
              highestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Highest ticket price.')
                .optional(),
              soldOut: z
                .boolean()
                .describe('Whether all tickets are sold for the event.')
                .optional()
                .nullable(),
              taxSettings: z
                .object({
                  type: z
                    .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                    .describe('Tax application settings.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Tax name.')
                    .min(1)
                    .max(10)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe('Tax rate. For example, `21.55`.')
                    .optional()
                    .nullable(),
                  appliedToDonations: z
                    .boolean()
                    .describe('Apply tax to donations.')
                    .optional()
                    .nullable(),
                })
                .describe('How tax is applied.')
                .optional(),
              reservationDurationInMinutes: z
                .number()
                .int()
                .describe('Reservation duration in minutes.')
                .min(5)
                .max(30)
                .optional()
                .nullable(),
            })
            .describe('Ticket registration details.')
            .optional(),
          external: z
            .object({
              url: z
                .string()
                .describe('External event registration URL.')
                .url()
                .optional()
                .nullable(),
            })
            .describe('External registration details.')
            .optional(),
          allowedGuestTypes: z
            .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
            .describe('Types of guests allowed to register.')
            .optional(),
          initialType: z
            .enum(['RSVP', 'TICKETING'])
            .describe(
              "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
            )
            .optional(),
          registrationPaused: z
            .boolean()
            .describe('Whether the registration is paused.')
            .optional(),
          registrationDisabled: z
            .boolean()
            .describe('Whether the registration is disabled.')
            .optional(),
        })
        .describe(
          'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
        )
        .optional(),
      calendarUrls: z
        .object({
          google: z
            .string()
            .describe('"Add to Google Calendar" URL.')
            .optional(),
          ics: z
            .string()
            .describe('"Download ICS calendar file" URL.')
            .optional(),
        })
        .describe(
          'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      eventPageUrl: z
        .string()
        .describe(
          'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
        )
        .optional(),
      form: z
        .object({
          controls: z
            .array(
              z.object({
                type: z
                  .enum([
                    'INPUT',
                    'TEXTAREA',
                    'DROPDOWN',
                    'RADIO',
                    'CHECKBOX',
                    'NAME',
                    'GUEST_CONTROL',
                    'ADDRESS_SHORT',
                    'ADDRESS_FULL',
                    'DATE',
                  ])
                  .describe('Field control type.')
                  .optional(),
                system: z
                  .boolean()
                  .describe(
                    'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Deprecated: Use `id` or `_id`.')
                  .optional(),
                inputs: z
                  .array(
                    z.object({
                      name: z.string().describe('Field name.').optional(),
                      array: z
                        .boolean()
                        .describe(
                          '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                        )
                        .optional(),
                      label: z
                        .string()
                        .describe('Main field label.')
                        .optional(),
                      additionalLabels: z
                        .record(z.string(), z.string())
                        .describe(
                          'Additional labels for multi-valued fields such as address.'
                        )
                        .optional(),
                      options: z.array(z.string()).optional(),
                      mandatory: z
                        .boolean()
                        .describe('Whether field is mandatory.')
                        .optional(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of accepted characters (relevant for text fields).'
                        )
                        .optional(),
                      type: z
                        .enum([
                          'TEXT',
                          'NUMBER',
                          'TEXT_ARRAY',
                          'DATE_TIME',
                          'ADDRESS',
                        ])
                        .describe(
                          'Type which determines field format.\nUsed to validate submitted response.'
                        )
                        .optional(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                        )
                        .optional()
                        .nullable(),
                      defaultOptionSelection: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z.never().optional(),
                            }),
                            z.object({
                              placeholderText: z.never().optional(),
                              optionIndex: z
                                .number()
                                .int()
                                .describe(
                                  '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                )
                                .min(0)
                                .max(199),
                            }),
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z
                                .string()
                                .describe(
                                  'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe(
                          'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                        )
                        .optional(),
                      labels: z
                        .array(
                          z.object({
                            name: z.string().describe('Field name.').optional(),
                            label: z
                              .string()
                              .describe('Field label.')
                              .optional(),
                          })
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                label: z
                  .string()
                  .describe('*Deprecated:** Use `controls.inputs.label`.')
                  .optional(),
                orderIndex: z
                  .number()
                  .int()
                  .describe(
                    'Field controls are sorted by this value in ascending order.'
                  )
                  .optional(),
                _id: z.string().describe('Unique control ID.').optional(),
                deleted: z
                  .boolean()
                  .describe('Whether the input control is deleted.')
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
          messages: z
            .object({
              rsvp: z
                .object({
                  rsvpYesOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"YES"`.'
                    )
                    .optional(),
                  rsvpNoOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"NO"`.'
                    )
                    .optional(),
                  positiveMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                    )
                    .optional(),
                  waitlistMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                    )
                    .optional(),
                  negativeMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for negative response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                    )
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('"Submit form" call-to-action label text.')
                    .optional(),
                })
                .describe(
                  '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                )
                .optional(),
              checkout: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for response.')
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('Submit form call-to-action label text.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      downloadTicketsLabel: z
                        .string()
                        .describe(
                          '"Download tickets" call-to-action label text.'
                        )
                        .optional(),
                      addToCalendarLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareEventLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after checkout.')
                    .optional(),
                })
                .describe('Checkout form messages.')
                .optional(),
              registrationClosed: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event registration is closed.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event registration is closed.')
                .optional(),
              ticketsUnavailable: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event tickets are unavailable.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event tickets are unavailable.')
                .optional(),
            })
            .describe(
              'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
            )
            .optional(),
        })
        .describe(
          'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
        )
        .optional(),
      summaries: z
        .object({
          rsvps: z
            .object({
              totalCount: z
                .number()
                .int()
                .describe('Total number of RSVPs.')
                .optional(),
              yesCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `YES`.')
                .optional(),
              noCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `NO`.')
                .optional(),
              waitlistCount: z
                .number()
                .int()
                .describe('Number of RSVPs in a waitlist.')
                .optional(),
            })
            .describe('RSVP summary of guests.')
            .optional(),
          tickets: z
            .object({
              ticketsSold: z
                .number()
                .int()
                .describe('Number of sold tickets.')
                .optional(),
              revenue: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Total revenue. Taxes and payment provider fees aren't deducted."
                )
                .optional(),
              currencyLocked: z
                .boolean()
                .describe(
                  "Whether the currency is locked and can't be changed."
                )
                .optional(),
              totalOrders: z
                .number()
                .int()
                .describe('Number of orders placed.')
                .optional(),
              totalSales: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Total balance of confirmed transactions.')
                .optional(),
            })
            .describe(
              "Summary of revenue and sold tickets. Archived orders aren't included."
            )
            .optional(),
        })
        .describe(
          'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
        )
        .optional(),
      instanceId: z
        .string()
        .describe('Instance ID of the site where the event is hosted.')
        .optional(),
      guestListSettings: z
        .object({
          displayedPublicly: z
            .boolean()
            .describe(
              'Whether the guest list is displayed publicly to all guests.'
            )
            .optional(),
        })
        .describe('Guest list configuration.')
        .optional(),
      userId: z
        .string()
        .describe('ID of the user who created the event.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      onlineConferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
            )
            .optional(),
          providerId: z
            .string()
            .describe('Conference host ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['MEETING', 'WEBINAR'])
            .describe('Conference type.')
            .optional(),
          session: z
            .object({
              hostLink: z
                .string()
                .describe(
                  'Link for the event host to start the online conference session.'
                )
                .optional(),
              guestLink: z
                .string()
                .describe(
                  'Link for guests to join the online conference session.'
                )
                .optional(),
              password: z
                .string()
                .describe(
                  'Password required to join the online conferencing session (when relevant).'
                )
                .optional()
                .nullable(),
              sessionCreated: z
                .boolean()
                .describe(
                  'Whether the session was created successfully on the event host side.'
                )
                .optional()
                .nullable(),
              sessionId: z
                .string()
                .describe('Unique session ID.')
                .optional()
                .nullable(),
            })
            .describe('Online conferencing session information.')
            .optional(),
        })
        .describe(
          'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
        )
        .optional(),
      seoSettings: z
        .object({
          slug: z.string().describe('URL slug.').max(130).optional(),
          advancedSeoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Advanced SEO data.')
            .optional(),
          hidden: z
            .boolean()
            .describe('Whether the slug is hidden from the SEO sitemap.')
            .optional()
            .nullable(),
        })
        .describe(
          'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
        )
        .optional(),
      contactLabel: z
        .string()
        .describe('Assigned contacts label key.')
        .optional()
        .nullable(),
      agendaSettings: z
        .object({
          enabled: z
            .boolean()
            .describe('Whether the schedule is enabled for the event.')
            .optional(),
          pageUrl: z.string().describe('Schedule page URL.').optional(),
        })
        .describe(
          'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
        )
        .optional(),
      eventDisplaySettings: z
        .object({
          hideEventDetailsButton: z
            .boolean()
            .describe(
              'Whether event details button is hidden. Only available for events with no registration.'
            )
            .optional()
            .nullable(),
          hideEventDetailsPage: z
            .boolean()
            .describe(
              'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
            )
            .optional()
            .nullable(),
        })
        .describe('Visual settings for event.')
        .optional(),
      description: z
        .any()
        .describe(
          'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
        )
        .optional(),
      publishedDate: z
        .date()
        .describe('Event publish timestamp.')
        .optional()
        .nullable(),
    })
    .describe('Published event.')
    .optional(),
});
export const CancelEventRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CancelEventResponse = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe(
          'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .max(500)
        .optional()
        .nullable(),
      detailedDescription: z
        .string()
        .describe(
          'Detailed description of an event.\n\nThis field is deprecated.'
        )
        .max(50000)
        .optional()
        .nullable(),
      mainImage: z
        .string()
        .describe(
          'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the event was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the event was updated.')
        .optional()
        .nullable(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
      registration: z
        .object({
          type: z
            .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
            .describe('Reflects the current state of the registration.')
            .optional(),
          status: z
            .enum([
              'UNKNOWN_REGISTRATION_STATUS',
              'CLOSED_AUTOMATICALLY',
              'CLOSED_MANUALLY',
              'OPEN_RSVP',
              'OPEN_RSVP_WAITLIST_ONLY',
              'OPEN_TICKETS',
              'OPEN_EXTERNAL',
              'SCHEDULED_RSVP',
            ])
            .describe('Registration status.')
            .optional(),
          rsvp: z
            .object({
              responseType: z
                .enum(['YES_ONLY', 'YES_AND_NO'])
                .describe('Available answers for registration to an event.')
                .optional(),
              limit: z
                .number()
                .int()
                .describe('How many guests can RSVP to an event.')
                .optional()
                .nullable(),
              waitlistEnabled: z
                .boolean()
                .describe(
                  'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                )
                .optional(),
              startDate: z
                .date()
                .describe('Registration start date.')
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe('Registration end date.')
                .optional()
                .nullable(),
            })
            .describe('RSVP registration details.')
            .optional(),
          tickets: z
            .object({
              guestsAssignedSeparately: z
                .boolean()
                .describe(
                  'Whether the registration form must be filled out separately for each ticket.'
                )
                .optional(),
              ticketLimitPerOrder: z
                .number()
                .int()
                .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
                .min(0)
                .max(50)
                .optional(),
              currency: z
                .string()
                .describe('Ticket price currency.')
                .optional()
                .nullable(),
              lowestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Lowest ticket price.')
                .optional(),
              highestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Highest ticket price.')
                .optional(),
              soldOut: z
                .boolean()
                .describe('Whether all tickets are sold for the event.')
                .optional()
                .nullable(),
              taxSettings: z
                .object({
                  type: z
                    .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                    .describe('Tax application settings.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Tax name.')
                    .min(1)
                    .max(10)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe('Tax rate. For example, `21.55`.')
                    .optional()
                    .nullable(),
                  appliedToDonations: z
                    .boolean()
                    .describe('Apply tax to donations.')
                    .optional()
                    .nullable(),
                })
                .describe('How tax is applied.')
                .optional(),
              reservationDurationInMinutes: z
                .number()
                .int()
                .describe('Reservation duration in minutes.')
                .min(5)
                .max(30)
                .optional()
                .nullable(),
            })
            .describe('Ticket registration details.')
            .optional(),
          external: z
            .object({
              url: z
                .string()
                .describe('External event registration URL.')
                .url()
                .optional()
                .nullable(),
            })
            .describe('External registration details.')
            .optional(),
          allowedGuestTypes: z
            .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
            .describe('Types of guests allowed to register.')
            .optional(),
          initialType: z
            .enum(['RSVP', 'TICKETING'])
            .describe(
              "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
            )
            .optional(),
          registrationPaused: z
            .boolean()
            .describe('Whether the registration is paused.')
            .optional(),
          registrationDisabled: z
            .boolean()
            .describe('Whether the registration is disabled.')
            .optional(),
        })
        .describe(
          'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
        )
        .optional(),
      calendarUrls: z
        .object({
          google: z
            .string()
            .describe('"Add to Google Calendar" URL.')
            .optional(),
          ics: z
            .string()
            .describe('"Download ICS calendar file" URL.')
            .optional(),
        })
        .describe(
          'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      eventPageUrl: z
        .string()
        .describe(
          'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
        )
        .optional(),
      form: z
        .object({
          controls: z
            .array(
              z.object({
                type: z
                  .enum([
                    'INPUT',
                    'TEXTAREA',
                    'DROPDOWN',
                    'RADIO',
                    'CHECKBOX',
                    'NAME',
                    'GUEST_CONTROL',
                    'ADDRESS_SHORT',
                    'ADDRESS_FULL',
                    'DATE',
                  ])
                  .describe('Field control type.')
                  .optional(),
                system: z
                  .boolean()
                  .describe(
                    'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Deprecated: Use `id` or `_id`.')
                  .optional(),
                inputs: z
                  .array(
                    z.object({
                      name: z.string().describe('Field name.').optional(),
                      array: z
                        .boolean()
                        .describe(
                          '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                        )
                        .optional(),
                      label: z
                        .string()
                        .describe('Main field label.')
                        .optional(),
                      additionalLabels: z
                        .record(z.string(), z.string())
                        .describe(
                          'Additional labels for multi-valued fields such as address.'
                        )
                        .optional(),
                      options: z.array(z.string()).optional(),
                      mandatory: z
                        .boolean()
                        .describe('Whether field is mandatory.')
                        .optional(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of accepted characters (relevant for text fields).'
                        )
                        .optional(),
                      type: z
                        .enum([
                          'TEXT',
                          'NUMBER',
                          'TEXT_ARRAY',
                          'DATE_TIME',
                          'ADDRESS',
                        ])
                        .describe(
                          'Type which determines field format.\nUsed to validate submitted response.'
                        )
                        .optional(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                        )
                        .optional()
                        .nullable(),
                      defaultOptionSelection: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z.never().optional(),
                            }),
                            z.object({
                              placeholderText: z.never().optional(),
                              optionIndex: z
                                .number()
                                .int()
                                .describe(
                                  '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                )
                                .min(0)
                                .max(199),
                            }),
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z
                                .string()
                                .describe(
                                  'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe(
                          'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                        )
                        .optional(),
                      labels: z
                        .array(
                          z.object({
                            name: z.string().describe('Field name.').optional(),
                            label: z
                              .string()
                              .describe('Field label.')
                              .optional(),
                          })
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                label: z
                  .string()
                  .describe('*Deprecated:** Use `controls.inputs.label`.')
                  .optional(),
                orderIndex: z
                  .number()
                  .int()
                  .describe(
                    'Field controls are sorted by this value in ascending order.'
                  )
                  .optional(),
                _id: z.string().describe('Unique control ID.').optional(),
                deleted: z
                  .boolean()
                  .describe('Whether the input control is deleted.')
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
          messages: z
            .object({
              rsvp: z
                .object({
                  rsvpYesOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"YES"`.'
                    )
                    .optional(),
                  rsvpNoOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"NO"`.'
                    )
                    .optional(),
                  positiveMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                    )
                    .optional(),
                  waitlistMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                    )
                    .optional(),
                  negativeMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for negative response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                    )
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('"Submit form" call-to-action label text.')
                    .optional(),
                })
                .describe(
                  '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                )
                .optional(),
              checkout: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for response.')
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('Submit form call-to-action label text.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      downloadTicketsLabel: z
                        .string()
                        .describe(
                          '"Download tickets" call-to-action label text.'
                        )
                        .optional(),
                      addToCalendarLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareEventLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after checkout.')
                    .optional(),
                })
                .describe('Checkout form messages.')
                .optional(),
              registrationClosed: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event registration is closed.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event registration is closed.')
                .optional(),
              ticketsUnavailable: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event tickets are unavailable.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event tickets are unavailable.')
                .optional(),
            })
            .describe(
              'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
            )
            .optional(),
        })
        .describe(
          'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
        )
        .optional(),
      summaries: z
        .object({
          rsvps: z
            .object({
              totalCount: z
                .number()
                .int()
                .describe('Total number of RSVPs.')
                .optional(),
              yesCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `YES`.')
                .optional(),
              noCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `NO`.')
                .optional(),
              waitlistCount: z
                .number()
                .int()
                .describe('Number of RSVPs in a waitlist.')
                .optional(),
            })
            .describe('RSVP summary of guests.')
            .optional(),
          tickets: z
            .object({
              ticketsSold: z
                .number()
                .int()
                .describe('Number of sold tickets.')
                .optional(),
              revenue: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Total revenue. Taxes and payment provider fees aren't deducted."
                )
                .optional(),
              currencyLocked: z
                .boolean()
                .describe(
                  "Whether the currency is locked and can't be changed."
                )
                .optional(),
              totalOrders: z
                .number()
                .int()
                .describe('Number of orders placed.')
                .optional(),
              totalSales: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Total balance of confirmed transactions.')
                .optional(),
            })
            .describe(
              "Summary of revenue and sold tickets. Archived orders aren't included."
            )
            .optional(),
        })
        .describe(
          'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
        )
        .optional(),
      instanceId: z
        .string()
        .describe('Instance ID of the site where the event is hosted.')
        .optional(),
      guestListSettings: z
        .object({
          displayedPublicly: z
            .boolean()
            .describe(
              'Whether the guest list is displayed publicly to all guests.'
            )
            .optional(),
        })
        .describe('Guest list configuration.')
        .optional(),
      userId: z
        .string()
        .describe('ID of the user who created the event.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      onlineConferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
            )
            .optional(),
          providerId: z
            .string()
            .describe('Conference host ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['MEETING', 'WEBINAR'])
            .describe('Conference type.')
            .optional(),
          session: z
            .object({
              hostLink: z
                .string()
                .describe(
                  'Link for the event host to start the online conference session.'
                )
                .optional(),
              guestLink: z
                .string()
                .describe(
                  'Link for guests to join the online conference session.'
                )
                .optional(),
              password: z
                .string()
                .describe(
                  'Password required to join the online conferencing session (when relevant).'
                )
                .optional()
                .nullable(),
              sessionCreated: z
                .boolean()
                .describe(
                  'Whether the session was created successfully on the event host side.'
                )
                .optional()
                .nullable(),
              sessionId: z
                .string()
                .describe('Unique session ID.')
                .optional()
                .nullable(),
            })
            .describe('Online conferencing session information.')
            .optional(),
        })
        .describe(
          'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
        )
        .optional(),
      seoSettings: z
        .object({
          slug: z.string().describe('URL slug.').max(130).optional(),
          advancedSeoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Advanced SEO data.')
            .optional(),
          hidden: z
            .boolean()
            .describe('Whether the slug is hidden from the SEO sitemap.')
            .optional()
            .nullable(),
        })
        .describe(
          'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
        )
        .optional(),
      contactLabel: z
        .string()
        .describe('Assigned contacts label key.')
        .optional()
        .nullable(),
      agendaSettings: z
        .object({
          enabled: z
            .boolean()
            .describe('Whether the schedule is enabled for the event.')
            .optional(),
          pageUrl: z.string().describe('Schedule page URL.').optional(),
        })
        .describe(
          'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
        )
        .optional(),
      eventDisplaySettings: z
        .object({
          hideEventDetailsButton: z
            .boolean()
            .describe(
              'Whether event details button is hidden. Only available for events with no registration.'
            )
            .optional()
            .nullable(),
          hideEventDetailsPage: z
            .boolean()
            .describe(
              'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
            )
            .optional()
            .nullable(),
        })
        .describe('Visual settings for event.')
        .optional(),
      description: z
        .any()
        .describe(
          'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
        )
        .optional(),
      publishedDate: z
        .date()
        .describe('Event publish timestamp.')
        .optional()
        .nullable(),
    })
    .describe('Canceled event.')
    .optional(),
});
export const BulkCancelEventsByFilterRequest = z.object({
  options: z
    .object({
      filter: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`'
              ),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(100)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(20)
              .optional(),
          }),
          z.xor([
            z.object({ paging: z.never().optional() }),
            z.object({
              paging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.'
                    )
                    .min(0)
                    .max(1000)
                    .optional()
                    .nullable(),
                  offset: z
                    .number()
                    .int()
                    .describe(
                      'Number of items to skip in the current sort order.'
                    )
                    .min(0)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Paging options. Can't be used together with `cursorPaging`."
                ),
            }),
          ])
        )
        .describe(
          'Filter. See [Query Events](https://dev.wix.com/docs/rest/business-solutions/events/events-v3/query-events) for a list of supported filters.'
        )
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const BulkCancelEventsByFilterResponse = z.object({});
export const DeleteEventRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteEventResponse = z.object({
  eventId: z
    .string()
    .describe('Deleted event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkDeleteEventsByFilterRequest = z.object({
  options: z
    .object({
      filter: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`'
              ),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(100)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(20)
              .optional(),
          }),
          z.xor([
            z.object({ paging: z.never().optional() }),
            z.object({
              paging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.'
                    )
                    .min(0)
                    .max(1000)
                    .optional()
                    .nullable(),
                  offset: z
                    .number()
                    .int()
                    .describe(
                      'Number of items to skip in the current sort order.'
                    )
                    .min(0)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Paging options. Can't be used together with `cursorPaging`."
                ),
            }),
          ])
        )
        .describe(
          'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.'
        )
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const BulkDeleteEventsByFilterResponse = z.object({});
export const QueryEventsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          'registration.initialType': z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          title: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          slug: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'dateAndTimeSettings.startDate': z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'dateAndTimeSettings.endDate': z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _id: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          userId: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'title',
                'slug',
                '_createdDate',
                '_updatedDate',
                'dateAndTimeSettings.startDate',
                'dateAndTimeSettings.endDate',
                '_id',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.'
    ),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
      includeDrafts: z
        .boolean()
        .describe(
          'Whether draft events should be returned in the response. <br>\n**Note:** This parameter requires the `WIX_EVENTS.READ_DRAFT_EVENTS` permission.'
        )
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const QueryEventsResponse = z.object({
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.'
        )
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Requested offset.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor pointing to the next page in the list of results.'
            )
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
  events: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Event ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        location: z
          .object({
            name: z
              .string()
              .describe(
                'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
              )
              .max(50)
              .optional()
              .nullable(),
            type: z
              .enum(['VENUE', 'ONLINE'])
              .describe('Location type.')
              .optional(),
            address: z
              .object({
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine1: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe('Exact location address.')
              .optional(),
            locationTbd: z
              .boolean()
              .describe('Whether the event location is TBD.')
              .optional()
              .nullable(),
          })
          .describe('Event location.')
          .optional(),
        dateAndTimeSettings: z
          .object({
            dateAndTimeTbd: z
              .boolean()
              .describe('Whether the event date and time are TBD.')
              .optional()
              .nullable(),
            dateAndTimeTbdMessage: z
              .string()
              .describe(
                'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
              )
              .max(100)
              .optional()
              .nullable(),
            startDate: z
              .date()
              .describe(
                'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
              )
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe(
                'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
              )
              .optional()
              .nullable(),
            timeZoneId: z
              .string()
              .describe(
                'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
              )
              .max(100)
              .optional()
              .nullable(),
            hideEndDate: z
              .boolean()
              .describe(
                'Whether the end date is hidden in the formatted date and time.'
              )
              .optional()
              .nullable(),
            showTimeZone: z
              .boolean()
              .describe(
                'Whether the time zone is displayed in the formatted schedule.'
              )
              .optional()
              .nullable(),
            recurrenceStatus: z
              .enum([
                'ONE_TIME',
                'RECURRING',
                'RECURRING_UPCOMING',
                'RECURRING_RECENTLY_ENDED',
                'RECURRING_RECENTLY_CANCELED',
              ])
              .describe('Repeating event status.')
              .optional(),
            recurringEvents: z
              .object({
                individualEventDates: z
                  .array(
                    z.object({
                      startDate: z
                        .date()
                        .describe('Event start date.')
                        .optional()
                        .nullable(),
                      endDate: z
                        .date()
                        .describe('Event end date.')
                        .optional()
                        .nullable(),
                      timeZoneId: z
                        .string()
                        .describe(
                          'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      showTimeZone: z
                        .boolean()
                        .describe(
                          'Whether the time zone is displayed in a formatted schedule.'
                        )
                        .optional(),
                    })
                  )
                  .max(1000)
                  .optional(),
                categoryId: z
                  .string()
                  .describe(
                    'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Event repetitions.')
              .optional(),
            formatted: z
              .object({
                dateAndTime: z
                  .string()
                  .describe(
                    'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                startDate: z
                  .string()
                  .describe(
                    'Formatted start date of the event. Empty for TBD schedules.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                startTime: z
                  .string()
                  .describe(
                    'Formatted start time of the event. Empty for TBD schedules.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                endDate: z
                  .string()
                  .describe(
                    'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                endTime: z
                  .string()
                  .describe(
                    'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
              })
              .describe('Formatted date and time settings.')
              .optional(),
          })
          .describe('Event date and time settings.')
          .optional(),
        title: z
          .string()
          .describe('Event title.')
          .min(1)
          .max(120)
          .optional()
          .nullable(),
        shortDescription: z
          .string()
          .describe(
            'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
          )
          .max(500)
          .optional()
          .nullable(),
        detailedDescription: z
          .string()
          .describe(
            'Detailed description of an event.\n\nThis field is deprecated.'
          )
          .max(50000)
          .optional()
          .nullable(),
        mainImage: z
          .string()
          .describe(
            'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
          )
          .optional(),
        slug: z
          .string()
          .describe(
            'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time when the event was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time when the event was updated.')
          .optional()
          .nullable(),
        status: z
          .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
          .describe('Event status.')
          .optional(),
        registration: z
          .object({
            type: z
              .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
              .describe('Reflects the current state of the registration.')
              .optional(),
            status: z
              .enum([
                'UNKNOWN_REGISTRATION_STATUS',
                'CLOSED_AUTOMATICALLY',
                'CLOSED_MANUALLY',
                'OPEN_RSVP',
                'OPEN_RSVP_WAITLIST_ONLY',
                'OPEN_TICKETS',
                'OPEN_EXTERNAL',
                'SCHEDULED_RSVP',
              ])
              .describe('Registration status.')
              .optional(),
            rsvp: z
              .object({
                responseType: z
                  .enum(['YES_ONLY', 'YES_AND_NO'])
                  .describe('Available answers for registration to an event.')
                  .optional(),
                limit: z
                  .number()
                  .int()
                  .describe('How many guests can RSVP to an event.')
                  .optional()
                  .nullable(),
                waitlistEnabled: z
                  .boolean()
                  .describe(
                    'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                  )
                  .optional(),
                startDate: z
                  .date()
                  .describe('Registration start date.')
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe('Registration end date.')
                  .optional()
                  .nullable(),
              })
              .describe('RSVP registration details.')
              .optional(),
            tickets: z
              .object({
                guestsAssignedSeparately: z
                  .boolean()
                  .describe(
                    'Whether the registration form must be filled out separately for each ticket.'
                  )
                  .optional(),
                ticketLimitPerOrder: z
                  .number()
                  .int()
                  .describe(
                    'Ticket limit per order. <br>\nDefault: 20 tickets.'
                  )
                  .min(0)
                  .max(50)
                  .optional(),
                currency: z
                  .string()
                  .describe('Ticket price currency.')
                  .optional()
                  .nullable(),
                lowestPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Lowest ticket price.')
                  .optional(),
                highestPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Highest ticket price.')
                  .optional(),
                soldOut: z
                  .boolean()
                  .describe('Whether all tickets are sold for the event.')
                  .optional()
                  .nullable(),
                taxSettings: z
                  .object({
                    type: z
                      .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                      .describe('Tax application settings.')
                      .optional(),
                    name: z
                      .string()
                      .describe('Tax name.')
                      .min(1)
                      .max(10)
                      .optional()
                      .nullable(),
                    rate: z
                      .string()
                      .describe('Tax rate. For example, `21.55`.')
                      .optional()
                      .nullable(),
                    appliedToDonations: z
                      .boolean()
                      .describe('Apply tax to donations.')
                      .optional()
                      .nullable(),
                  })
                  .describe('How tax is applied.')
                  .optional(),
                reservationDurationInMinutes: z
                  .number()
                  .int()
                  .describe('Reservation duration in minutes.')
                  .min(5)
                  .max(30)
                  .optional()
                  .nullable(),
              })
              .describe('Ticket registration details.')
              .optional(),
            external: z
              .object({
                url: z
                  .string()
                  .describe('External event registration URL.')
                  .url()
                  .optional()
                  .nullable(),
              })
              .describe('External registration details.')
              .optional(),
            allowedGuestTypes: z
              .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
              .describe('Types of guests allowed to register.')
              .optional(),
            initialType: z
              .enum(['RSVP', 'TICKETING'])
              .describe(
                "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
              )
              .optional(),
            registrationPaused: z
              .boolean()
              .describe('Whether the registration is paused.')
              .optional(),
            registrationDisabled: z
              .boolean()
              .describe('Whether the registration is disabled.')
              .optional(),
          })
          .describe(
            'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
          )
          .optional(),
        calendarUrls: z
          .object({
            google: z
              .string()
              .describe('"Add to Google Calendar" URL.')
              .optional(),
            ics: z
              .string()
              .describe('"Download ICS calendar file" URL.')
              .optional(),
          })
          .describe(
            'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
          )
          .optional(),
        eventPageUrl: z
          .string()
          .describe(
            'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
          )
          .optional(),
        form: z
          .object({
            controls: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'INPUT',
                      'TEXTAREA',
                      'DROPDOWN',
                      'RADIO',
                      'CHECKBOX',
                      'NAME',
                      'GUEST_CONTROL',
                      'ADDRESS_SHORT',
                      'ADDRESS_FULL',
                      'DATE',
                    ])
                    .describe('Field control type.')
                    .optional(),
                  system: z
                    .boolean()
                    .describe(
                      'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                    )
                    .optional(),
                  name: z
                    .string()
                    .describe('Deprecated: Use `id` or `_id`.')
                    .optional(),
                  inputs: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        array: z
                          .boolean()
                          .describe(
                            '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                          )
                          .optional(),
                        label: z
                          .string()
                          .describe('Main field label.')
                          .optional(),
                        additionalLabels: z
                          .record(z.string(), z.string())
                          .describe(
                            'Additional labels for multi-valued fields such as address.'
                          )
                          .optional(),
                        options: z.array(z.string()).optional(),
                        mandatory: z
                          .boolean()
                          .describe('Whether field is mandatory.')
                          .optional(),
                        maxLength: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of accepted characters (relevant for text fields).'
                          )
                          .optional(),
                        type: z
                          .enum([
                            'TEXT',
                            'NUMBER',
                            'TEXT_ARRAY',
                            'DATE_TIME',
                            'ADDRESS',
                          ])
                          .describe(
                            'Type which determines field format.\nUsed to validate submitted response.'
                          )
                          .optional(),
                        maxSize: z
                          .number()
                          .int()
                          .describe(
                            'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                          )
                          .optional()
                          .nullable(),
                        defaultOptionSelection: z
                          .intersection(
                            z.object({}),
                            z.xor([
                              z.object({
                                optionIndex: z.never().optional(),
                                placeholderText: z.never().optional(),
                              }),
                              z.object({
                                placeholderText: z.never().optional(),
                                optionIndex: z
                                  .number()
                                  .int()
                                  .describe(
                                    '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                  )
                                  .min(0)
                                  .max(199),
                              }),
                              z.object({
                                optionIndex: z.never().optional(),
                                placeholderText: z
                                  .string()
                                  .describe(
                                    'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                  )
                                  .max(200),
                              }),
                            ])
                          )
                          .describe(
                            'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                          )
                          .optional(),
                        labels: z
                          .array(
                            z.object({
                              name: z
                                .string()
                                .describe('Field name.')
                                .optional(),
                              label: z
                                .string()
                                .describe('Field label.')
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  label: z
                    .string()
                    .describe('*Deprecated:** Use `controls.inputs.label`.')
                    .optional(),
                  orderIndex: z
                    .number()
                    .int()
                    .describe(
                      'Field controls are sorted by this value in ascending order.'
                    )
                    .optional(),
                  _id: z.string().describe('Unique control ID.').optional(),
                  deleted: z
                    .boolean()
                    .describe('Whether the input control is deleted.')
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
            messages: z
              .object({
                rsvp: z
                  .object({
                    rsvpYesOption: z
                      .string()
                      .describe(
                        'Label text indicating RSVP\'s `status` is `"YES"`.'
                      )
                      .optional(),
                    rsvpNoOption: z
                      .string()
                      .describe(
                        'Label text indicating RSVP\'s `status` is `"NO"`.'
                      )
                      .optional(),
                    positiveMessages: z
                      .object({
                        title: z
                          .string()
                          .describe('Main form title for positive response.')
                          .optional(),
                        confirmation: z
                          .object({
                            title: z
                              .string()
                              .describe('Confirmation message title.')
                              .optional(),
                            message: z
                              .string()
                              .describe('Confirmation message text.')
                              .optional(),
                            addToCalendarActionLabel: z
                              .string()
                              .describe(
                                '"Add to calendar" call-to-action label text.'
                              )
                              .optional(),
                            shareActionLabel: z
                              .string()
                              .describe(
                                '"Share event" call-to-action label text.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Confirmation messages shown after registration.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                      )
                      .optional(),
                    waitlistMessages: z
                      .object({
                        title: z
                          .string()
                          .describe('Main form title for positive response.')
                          .optional(),
                        confirmation: z
                          .object({
                            title: z
                              .string()
                              .describe('Confirmation message title.')
                              .optional(),
                            message: z
                              .string()
                              .describe('Confirmation message text.')
                              .optional(),
                            addToCalendarActionLabel: z
                              .string()
                              .describe(
                                '"Add to calendar" call-to-action label text.'
                              )
                              .optional(),
                            shareActionLabel: z
                              .string()
                              .describe(
                                '"Share event" call-to-action label text.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Confirmation messages shown after registration.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                      )
                      .optional(),
                    negativeMessages: z
                      .object({
                        title: z
                          .string()
                          .describe('Main form title for negative response.')
                          .optional(),
                        confirmation: z
                          .object({
                            title: z
                              .string()
                              .describe('Confirmation message title.')
                              .optional(),
                            shareActionLabel: z
                              .string()
                              .describe(
                                '"Share event" call-to-action label text.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Confirmation messages shown after registration.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                      )
                      .optional(),
                    submitActionLabel: z
                      .string()
                      .describe('"Submit form" call-to-action label text.')
                      .optional(),
                  })
                  .describe(
                    '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                  )
                  .optional(),
                checkout: z
                  .object({
                    title: z
                      .string()
                      .describe('Main form title for response.')
                      .optional(),
                    submitActionLabel: z
                      .string()
                      .describe('Submit form call-to-action label text.')
                      .optional(),
                    confirmation: z
                      .object({
                        title: z
                          .string()
                          .describe('Confirmation message title.')
                          .optional(),
                        message: z
                          .string()
                          .describe('Confirmation message text.')
                          .optional(),
                        downloadTicketsLabel: z
                          .string()
                          .describe(
                            '"Download tickets" call-to-action label text.'
                          )
                          .optional(),
                        addToCalendarLabel: z
                          .string()
                          .describe(
                            '"Add to calendar" call-to-action label text.'
                          )
                          .optional(),
                        shareEventLabel: z
                          .string()
                          .describe('"Share event" call-to-action label text.')
                          .optional(),
                      })
                      .describe('Confirmation messages shown after checkout.')
                      .optional(),
                  })
                  .describe('Checkout form messages.')
                  .optional(),
                registrationClosed: z
                  .object({
                    message: z
                      .string()
                      .describe(
                        'Message shown when event registration is closed.'
                      )
                      .optional(),
                    exploreEventsActionLabel: z
                      .string()
                      .describe(
                        '"Explore other events" call-to-action label text.'
                      )
                      .optional(),
                  })
                  .describe('Messages shown when event registration is closed.')
                  .optional(),
                ticketsUnavailable: z
                  .object({
                    message: z
                      .string()
                      .describe(
                        'Message shown when event tickets are unavailable.'
                      )
                      .optional(),
                    exploreEventsActionLabel: z
                      .string()
                      .describe(
                        '"Explore other events" call-to-action label text.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Messages shown when event tickets are unavailable.'
                  )
                  .optional(),
              })
              .describe(
                'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
              )
              .optional(),
          })
          .describe(
            'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
          )
          .optional(),
        summaries: z
          .object({
            rsvps: z
              .object({
                totalCount: z
                  .number()
                  .int()
                  .describe('Total number of RSVPs.')
                  .optional(),
                yesCount: z
                  .number()
                  .int()
                  .describe('Number of RSVPs with status `YES`.')
                  .optional(),
                noCount: z
                  .number()
                  .int()
                  .describe('Number of RSVPs with status `NO`.')
                  .optional(),
                waitlistCount: z
                  .number()
                  .int()
                  .describe('Number of RSVPs in a waitlist.')
                  .optional(),
              })
              .describe('RSVP summary of guests.')
              .optional(),
            tickets: z
              .object({
                ticketsSold: z
                  .number()
                  .int()
                  .describe('Number of sold tickets.')
                  .optional(),
                revenue: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Total revenue. Taxes and payment provider fees aren't deducted."
                  )
                  .optional(),
                currencyLocked: z
                  .boolean()
                  .describe(
                    "Whether the currency is locked and can't be changed."
                  )
                  .optional(),
                totalOrders: z
                  .number()
                  .int()
                  .describe('Number of orders placed.')
                  .optional(),
                totalSales: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Total balance of confirmed transactions.')
                  .optional(),
              })
              .describe(
                "Summary of revenue and sold tickets. Archived orders aren't included."
              )
              .optional(),
          })
          .describe(
            'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
          )
          .optional(),
        instanceId: z
          .string()
          .describe('Instance ID of the site where the event is hosted.')
          .optional(),
        guestListSettings: z
          .object({
            displayedPublicly: z
              .boolean()
              .describe(
                'Whether the guest list is displayed publicly to all guests.'
              )
              .optional(),
          })
          .describe('Guest list configuration.')
          .optional(),
        userId: z
          .string()
          .describe('ID of the user who created the event.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        onlineConferencing: z
          .object({
            enabled: z
              .boolean()
              .describe(
                'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
              )
              .optional(),
            providerId: z
              .string()
              .describe('Conference host ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['MEETING', 'WEBINAR'])
              .describe('Conference type.')
              .optional(),
            session: z
              .object({
                hostLink: z
                  .string()
                  .describe(
                    'Link for the event host to start the online conference session.'
                  )
                  .optional(),
                guestLink: z
                  .string()
                  .describe(
                    'Link for guests to join the online conference session.'
                  )
                  .optional(),
                password: z
                  .string()
                  .describe(
                    'Password required to join the online conferencing session (when relevant).'
                  )
                  .optional()
                  .nullable(),
                sessionCreated: z
                  .boolean()
                  .describe(
                    'Whether the session was created successfully on the event host side.'
                  )
                  .optional()
                  .nullable(),
                sessionId: z
                  .string()
                  .describe('Unique session ID.')
                  .optional()
                  .nullable(),
              })
              .describe('Online conferencing session information.')
              .optional(),
          })
          .describe(
            'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
          )
          .optional(),
        seoSettings: z
          .object({
            slug: z.string().describe('URL slug.').max(130).optional(),
            advancedSeoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Advanced SEO data.')
              .optional(),
            hidden: z
              .boolean()
              .describe('Whether the slug is hidden from the SEO sitemap.')
              .optional()
              .nullable(),
          })
          .describe(
            'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
          )
          .optional(),
        contactLabel: z
          .string()
          .describe('Assigned contacts label key.')
          .optional()
          .nullable(),
        agendaSettings: z
          .object({
            enabled: z
              .boolean()
              .describe('Whether the schedule is enabled for the event.')
              .optional(),
            pageUrl: z.string().describe('Schedule page URL.').optional(),
          })
          .describe(
            'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
          )
          .optional(),
        eventDisplaySettings: z
          .object({
            hideEventDetailsButton: z
              .boolean()
              .describe(
                'Whether event details button is hidden. Only available for events with no registration.'
              )
              .optional()
              .nullable(),
            hideEventDetailsPage: z
              .boolean()
              .describe(
                'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
              )
              .optional()
              .nullable(),
          })
          .describe('Visual settings for event.')
          .optional(),
        description: z
          .any()
          .describe(
            'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
          )
          .optional(),
        publishedDate: z
          .date()
          .describe('Event publish timestamp.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const CountEventsByStatusRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`.'
        )
        .optional()
        .nullable(),
      facet: z.array(z.string()).max(20).optional(),
      includeDrafts: z
        .boolean()
        .describe(
          'Whether draft events should be returned in the response. <br>\n**Note:** This parameter requires the `WIX_EVENTS.READ_DRAFT_EVENTS` permission.'
        )
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CountEventsByStatusResponse = z.object({
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.'
        )
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Requested offset.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor pointing to the next page in the list of results.'
            )
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value')
          .optional(),
      })
    )
    .describe('Filter facets.')
    .optional(),
});
export const ListEventsByCategoryRequest = z.object({
  categoryId: z
    .string()
    .describe('Category ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.'
            )
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Paging options to limit and skip the number of items.')
        .optional(),
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const ListEventsByCategoryResponse = z.object({
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.'
        )
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Requested offset.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor pointing to the next page in the list of results.'
            )
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
  events: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Event ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        location: z
          .object({
            name: z
              .string()
              .describe(
                'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
              )
              .max(50)
              .optional()
              .nullable(),
            type: z
              .enum(['VENUE', 'ONLINE'])
              .describe('Location type.')
              .optional(),
            address: z
              .object({
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine1: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe('Exact location address.')
              .optional(),
            locationTbd: z
              .boolean()
              .describe('Whether the event location is TBD.')
              .optional()
              .nullable(),
          })
          .describe('Event location.')
          .optional(),
        dateAndTimeSettings: z
          .object({
            dateAndTimeTbd: z
              .boolean()
              .describe('Whether the event date and time are TBD.')
              .optional()
              .nullable(),
            dateAndTimeTbdMessage: z
              .string()
              .describe(
                'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
              )
              .max(100)
              .optional()
              .nullable(),
            startDate: z
              .date()
              .describe(
                'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
              )
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe(
                'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
              )
              .optional()
              .nullable(),
            timeZoneId: z
              .string()
              .describe(
                'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
              )
              .max(100)
              .optional()
              .nullable(),
            hideEndDate: z
              .boolean()
              .describe(
                'Whether the end date is hidden in the formatted date and time.'
              )
              .optional()
              .nullable(),
            showTimeZone: z
              .boolean()
              .describe(
                'Whether the time zone is displayed in the formatted schedule.'
              )
              .optional()
              .nullable(),
            recurrenceStatus: z
              .enum([
                'ONE_TIME',
                'RECURRING',
                'RECURRING_UPCOMING',
                'RECURRING_RECENTLY_ENDED',
                'RECURRING_RECENTLY_CANCELED',
              ])
              .describe('Repeating event status.')
              .optional(),
            recurringEvents: z
              .object({
                individualEventDates: z
                  .array(
                    z.object({
                      startDate: z
                        .date()
                        .describe('Event start date.')
                        .optional()
                        .nullable(),
                      endDate: z
                        .date()
                        .describe('Event end date.')
                        .optional()
                        .nullable(),
                      timeZoneId: z
                        .string()
                        .describe(
                          'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      showTimeZone: z
                        .boolean()
                        .describe(
                          'Whether the time zone is displayed in a formatted schedule.'
                        )
                        .optional(),
                    })
                  )
                  .max(1000)
                  .optional(),
                categoryId: z
                  .string()
                  .describe(
                    'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Event repetitions.')
              .optional(),
            formatted: z
              .object({
                dateAndTime: z
                  .string()
                  .describe(
                    'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                startDate: z
                  .string()
                  .describe(
                    'Formatted start date of the event. Empty for TBD schedules.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                startTime: z
                  .string()
                  .describe(
                    'Formatted start time of the event. Empty for TBD schedules.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                endDate: z
                  .string()
                  .describe(
                    'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                endTime: z
                  .string()
                  .describe(
                    'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
              })
              .describe('Formatted date and time settings.')
              .optional(),
          })
          .describe('Event date and time settings.')
          .optional(),
        title: z
          .string()
          .describe('Event title.')
          .min(1)
          .max(120)
          .optional()
          .nullable(),
        shortDescription: z
          .string()
          .describe(
            'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
          )
          .max(500)
          .optional()
          .nullable(),
        detailedDescription: z
          .string()
          .describe(
            'Detailed description of an event.\n\nThis field is deprecated.'
          )
          .max(50000)
          .optional()
          .nullable(),
        mainImage: z
          .string()
          .describe(
            'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
          )
          .optional(),
        slug: z
          .string()
          .describe(
            'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time when the event was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time when the event was updated.')
          .optional()
          .nullable(),
        status: z
          .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
          .describe('Event status.')
          .optional(),
        registration: z
          .object({
            type: z
              .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
              .describe('Reflects the current state of the registration.')
              .optional(),
            status: z
              .enum([
                'UNKNOWN_REGISTRATION_STATUS',
                'CLOSED_AUTOMATICALLY',
                'CLOSED_MANUALLY',
                'OPEN_RSVP',
                'OPEN_RSVP_WAITLIST_ONLY',
                'OPEN_TICKETS',
                'OPEN_EXTERNAL',
                'SCHEDULED_RSVP',
              ])
              .describe('Registration status.')
              .optional(),
            rsvp: z
              .object({
                responseType: z
                  .enum(['YES_ONLY', 'YES_AND_NO'])
                  .describe('Available answers for registration to an event.')
                  .optional(),
                limit: z
                  .number()
                  .int()
                  .describe('How many guests can RSVP to an event.')
                  .optional()
                  .nullable(),
                waitlistEnabled: z
                  .boolean()
                  .describe(
                    'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                  )
                  .optional(),
                startDate: z
                  .date()
                  .describe('Registration start date.')
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe('Registration end date.')
                  .optional()
                  .nullable(),
              })
              .describe('RSVP registration details.')
              .optional(),
            tickets: z
              .object({
                guestsAssignedSeparately: z
                  .boolean()
                  .describe(
                    'Whether the registration form must be filled out separately for each ticket.'
                  )
                  .optional(),
                ticketLimitPerOrder: z
                  .number()
                  .int()
                  .describe(
                    'Ticket limit per order. <br>\nDefault: 20 tickets.'
                  )
                  .min(0)
                  .max(50)
                  .optional(),
                currency: z
                  .string()
                  .describe('Ticket price currency.')
                  .optional()
                  .nullable(),
                lowestPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Lowest ticket price.')
                  .optional(),
                highestPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Highest ticket price.')
                  .optional(),
                soldOut: z
                  .boolean()
                  .describe('Whether all tickets are sold for the event.')
                  .optional()
                  .nullable(),
                taxSettings: z
                  .object({
                    type: z
                      .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                      .describe('Tax application settings.')
                      .optional(),
                    name: z
                      .string()
                      .describe('Tax name.')
                      .min(1)
                      .max(10)
                      .optional()
                      .nullable(),
                    rate: z
                      .string()
                      .describe('Tax rate. For example, `21.55`.')
                      .optional()
                      .nullable(),
                    appliedToDonations: z
                      .boolean()
                      .describe('Apply tax to donations.')
                      .optional()
                      .nullable(),
                  })
                  .describe('How tax is applied.')
                  .optional(),
                reservationDurationInMinutes: z
                  .number()
                  .int()
                  .describe('Reservation duration in minutes.')
                  .min(5)
                  .max(30)
                  .optional()
                  .nullable(),
              })
              .describe('Ticket registration details.')
              .optional(),
            external: z
              .object({
                url: z
                  .string()
                  .describe('External event registration URL.')
                  .url()
                  .optional()
                  .nullable(),
              })
              .describe('External registration details.')
              .optional(),
            allowedGuestTypes: z
              .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
              .describe('Types of guests allowed to register.')
              .optional(),
            initialType: z
              .enum(['RSVP', 'TICKETING'])
              .describe(
                "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
              )
              .optional(),
            registrationPaused: z
              .boolean()
              .describe('Whether the registration is paused.')
              .optional(),
            registrationDisabled: z
              .boolean()
              .describe('Whether the registration is disabled.')
              .optional(),
          })
          .describe(
            'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
          )
          .optional(),
        calendarUrls: z
          .object({
            google: z
              .string()
              .describe('"Add to Google Calendar" URL.')
              .optional(),
            ics: z
              .string()
              .describe('"Download ICS calendar file" URL.')
              .optional(),
          })
          .describe(
            'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
          )
          .optional(),
        eventPageUrl: z
          .string()
          .describe(
            'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
          )
          .optional(),
        form: z
          .object({
            controls: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'INPUT',
                      'TEXTAREA',
                      'DROPDOWN',
                      'RADIO',
                      'CHECKBOX',
                      'NAME',
                      'GUEST_CONTROL',
                      'ADDRESS_SHORT',
                      'ADDRESS_FULL',
                      'DATE',
                    ])
                    .describe('Field control type.')
                    .optional(),
                  system: z
                    .boolean()
                    .describe(
                      'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                    )
                    .optional(),
                  name: z
                    .string()
                    .describe('Deprecated: Use `id` or `_id`.')
                    .optional(),
                  inputs: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        array: z
                          .boolean()
                          .describe(
                            '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                          )
                          .optional(),
                        label: z
                          .string()
                          .describe('Main field label.')
                          .optional(),
                        additionalLabels: z
                          .record(z.string(), z.string())
                          .describe(
                            'Additional labels for multi-valued fields such as address.'
                          )
                          .optional(),
                        options: z.array(z.string()).optional(),
                        mandatory: z
                          .boolean()
                          .describe('Whether field is mandatory.')
                          .optional(),
                        maxLength: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of accepted characters (relevant for text fields).'
                          )
                          .optional(),
                        type: z
                          .enum([
                            'TEXT',
                            'NUMBER',
                            'TEXT_ARRAY',
                            'DATE_TIME',
                            'ADDRESS',
                          ])
                          .describe(
                            'Type which determines field format.\nUsed to validate submitted response.'
                          )
                          .optional(),
                        maxSize: z
                          .number()
                          .int()
                          .describe(
                            'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                          )
                          .optional()
                          .nullable(),
                        defaultOptionSelection: z
                          .intersection(
                            z.object({}),
                            z.xor([
                              z.object({
                                optionIndex: z.never().optional(),
                                placeholderText: z.never().optional(),
                              }),
                              z.object({
                                placeholderText: z.never().optional(),
                                optionIndex: z
                                  .number()
                                  .int()
                                  .describe(
                                    '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                  )
                                  .min(0)
                                  .max(199),
                              }),
                              z.object({
                                optionIndex: z.never().optional(),
                                placeholderText: z
                                  .string()
                                  .describe(
                                    'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                  )
                                  .max(200),
                              }),
                            ])
                          )
                          .describe(
                            'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                          )
                          .optional(),
                        labels: z
                          .array(
                            z.object({
                              name: z
                                .string()
                                .describe('Field name.')
                                .optional(),
                              label: z
                                .string()
                                .describe('Field label.')
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  label: z
                    .string()
                    .describe('*Deprecated:** Use `controls.inputs.label`.')
                    .optional(),
                  orderIndex: z
                    .number()
                    .int()
                    .describe(
                      'Field controls are sorted by this value in ascending order.'
                    )
                    .optional(),
                  _id: z.string().describe('Unique control ID.').optional(),
                  deleted: z
                    .boolean()
                    .describe('Whether the input control is deleted.')
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
            messages: z
              .object({
                rsvp: z
                  .object({
                    rsvpYesOption: z
                      .string()
                      .describe(
                        'Label text indicating RSVP\'s `status` is `"YES"`.'
                      )
                      .optional(),
                    rsvpNoOption: z
                      .string()
                      .describe(
                        'Label text indicating RSVP\'s `status` is `"NO"`.'
                      )
                      .optional(),
                    positiveMessages: z
                      .object({
                        title: z
                          .string()
                          .describe('Main form title for positive response.')
                          .optional(),
                        confirmation: z
                          .object({
                            title: z
                              .string()
                              .describe('Confirmation message title.')
                              .optional(),
                            message: z
                              .string()
                              .describe('Confirmation message text.')
                              .optional(),
                            addToCalendarActionLabel: z
                              .string()
                              .describe(
                                '"Add to calendar" call-to-action label text.'
                              )
                              .optional(),
                            shareActionLabel: z
                              .string()
                              .describe(
                                '"Share event" call-to-action label text.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Confirmation messages shown after registration.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                      )
                      .optional(),
                    waitlistMessages: z
                      .object({
                        title: z
                          .string()
                          .describe('Main form title for positive response.')
                          .optional(),
                        confirmation: z
                          .object({
                            title: z
                              .string()
                              .describe('Confirmation message title.')
                              .optional(),
                            message: z
                              .string()
                              .describe('Confirmation message text.')
                              .optional(),
                            addToCalendarActionLabel: z
                              .string()
                              .describe(
                                '"Add to calendar" call-to-action label text.'
                              )
                              .optional(),
                            shareActionLabel: z
                              .string()
                              .describe(
                                '"Share event" call-to-action label text.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Confirmation messages shown after registration.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                      )
                      .optional(),
                    negativeMessages: z
                      .object({
                        title: z
                          .string()
                          .describe('Main form title for negative response.')
                          .optional(),
                        confirmation: z
                          .object({
                            title: z
                              .string()
                              .describe('Confirmation message title.')
                              .optional(),
                            shareActionLabel: z
                              .string()
                              .describe(
                                '"Share event" call-to-action label text.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Confirmation messages shown after registration.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                      )
                      .optional(),
                    submitActionLabel: z
                      .string()
                      .describe('"Submit form" call-to-action label text.')
                      .optional(),
                  })
                  .describe(
                    '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                  )
                  .optional(),
                checkout: z
                  .object({
                    title: z
                      .string()
                      .describe('Main form title for response.')
                      .optional(),
                    submitActionLabel: z
                      .string()
                      .describe('Submit form call-to-action label text.')
                      .optional(),
                    confirmation: z
                      .object({
                        title: z
                          .string()
                          .describe('Confirmation message title.')
                          .optional(),
                        message: z
                          .string()
                          .describe('Confirmation message text.')
                          .optional(),
                        downloadTicketsLabel: z
                          .string()
                          .describe(
                            '"Download tickets" call-to-action label text.'
                          )
                          .optional(),
                        addToCalendarLabel: z
                          .string()
                          .describe(
                            '"Add to calendar" call-to-action label text.'
                          )
                          .optional(),
                        shareEventLabel: z
                          .string()
                          .describe('"Share event" call-to-action label text.')
                          .optional(),
                      })
                      .describe('Confirmation messages shown after checkout.')
                      .optional(),
                  })
                  .describe('Checkout form messages.')
                  .optional(),
                registrationClosed: z
                  .object({
                    message: z
                      .string()
                      .describe(
                        'Message shown when event registration is closed.'
                      )
                      .optional(),
                    exploreEventsActionLabel: z
                      .string()
                      .describe(
                        '"Explore other events" call-to-action label text.'
                      )
                      .optional(),
                  })
                  .describe('Messages shown when event registration is closed.')
                  .optional(),
                ticketsUnavailable: z
                  .object({
                    message: z
                      .string()
                      .describe(
                        'Message shown when event tickets are unavailable.'
                      )
                      .optional(),
                    exploreEventsActionLabel: z
                      .string()
                      .describe(
                        '"Explore other events" call-to-action label text.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Messages shown when event tickets are unavailable.'
                  )
                  .optional(),
              })
              .describe(
                'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
              )
              .optional(),
          })
          .describe(
            'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
          )
          .optional(),
        summaries: z
          .object({
            rsvps: z
              .object({
                totalCount: z
                  .number()
                  .int()
                  .describe('Total number of RSVPs.')
                  .optional(),
                yesCount: z
                  .number()
                  .int()
                  .describe('Number of RSVPs with status `YES`.')
                  .optional(),
                noCount: z
                  .number()
                  .int()
                  .describe('Number of RSVPs with status `NO`.')
                  .optional(),
                waitlistCount: z
                  .number()
                  .int()
                  .describe('Number of RSVPs in a waitlist.')
                  .optional(),
              })
              .describe('RSVP summary of guests.')
              .optional(),
            tickets: z
              .object({
                ticketsSold: z
                  .number()
                  .int()
                  .describe('Number of sold tickets.')
                  .optional(),
                revenue: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Total revenue. Taxes and payment provider fees aren't deducted."
                  )
                  .optional(),
                currencyLocked: z
                  .boolean()
                  .describe(
                    "Whether the currency is locked and can't be changed."
                  )
                  .optional(),
                totalOrders: z
                  .number()
                  .int()
                  .describe('Number of orders placed.')
                  .optional(),
                totalSales: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Total balance of confirmed transactions.')
                  .optional(),
              })
              .describe(
                "Summary of revenue and sold tickets. Archived orders aren't included."
              )
              .optional(),
          })
          .describe(
            'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
          )
          .optional(),
        instanceId: z
          .string()
          .describe('Instance ID of the site where the event is hosted.')
          .optional(),
        guestListSettings: z
          .object({
            displayedPublicly: z
              .boolean()
              .describe(
                'Whether the guest list is displayed publicly to all guests.'
              )
              .optional(),
          })
          .describe('Guest list configuration.')
          .optional(),
        userId: z
          .string()
          .describe('ID of the user who created the event.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        onlineConferencing: z
          .object({
            enabled: z
              .boolean()
              .describe(
                'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
              )
              .optional(),
            providerId: z
              .string()
              .describe('Conference host ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['MEETING', 'WEBINAR'])
              .describe('Conference type.')
              .optional(),
            session: z
              .object({
                hostLink: z
                  .string()
                  .describe(
                    'Link for the event host to start the online conference session.'
                  )
                  .optional(),
                guestLink: z
                  .string()
                  .describe(
                    'Link for guests to join the online conference session.'
                  )
                  .optional(),
                password: z
                  .string()
                  .describe(
                    'Password required to join the online conferencing session (when relevant).'
                  )
                  .optional()
                  .nullable(),
                sessionCreated: z
                  .boolean()
                  .describe(
                    'Whether the session was created successfully on the event host side.'
                  )
                  .optional()
                  .nullable(),
                sessionId: z
                  .string()
                  .describe('Unique session ID.')
                  .optional()
                  .nullable(),
              })
              .describe('Online conferencing session information.')
              .optional(),
          })
          .describe(
            'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
          )
          .optional(),
        seoSettings: z
          .object({
            slug: z.string().describe('URL slug.').max(130).optional(),
            advancedSeoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Advanced SEO data.')
              .optional(),
            hidden: z
              .boolean()
              .describe('Whether the slug is hidden from the SEO sitemap.')
              .optional()
              .nullable(),
          })
          .describe(
            'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
          )
          .optional(),
        contactLabel: z
          .string()
          .describe('Assigned contacts label key.')
          .optional()
          .nullable(),
        agendaSettings: z
          .object({
            enabled: z
              .boolean()
              .describe('Whether the schedule is enabled for the event.')
              .optional(),
            pageUrl: z.string().describe('Schedule page URL.').optional(),
          })
          .describe(
            'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
          )
          .optional(),
        eventDisplaySettings: z
          .object({
            hideEventDetailsButton: z
              .boolean()
              .describe(
                'Whether event details button is hidden. Only available for events with no registration.'
              )
              .optional()
              .nullable(),
            hideEventDetailsPage: z
              .boolean()
              .describe(
                'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
              )
              .optional()
              .nullable(),
          })
          .describe('Visual settings for event.')
          .optional(),
        description: z
          .any()
          .describe(
            'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
          )
          .optional(),
        publishedDate: z
          .date()
          .describe('Event publish timestamp.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const GetEventRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const GetEventResponse = z.object({
  _id: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  location: z
    .object({
      name: z
        .string()
        .describe(
          'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
        )
        .max(50)
        .optional()
        .nullable(),
      type: z.enum(['VENUE', 'ONLINE']).describe('Location type.').optional(),
      address: z
        .object({
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine1: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Exact location address.')
        .optional(),
      locationTbd: z
        .boolean()
        .describe('Whether the event location is TBD.')
        .optional()
        .nullable(),
    })
    .describe('Event location.')
    .optional(),
  dateAndTimeSettings: z
    .object({
      dateAndTimeTbd: z
        .boolean()
        .describe('Whether the event date and time are TBD.')
        .optional()
        .nullable(),
      dateAndTimeTbdMessage: z
        .string()
        .describe(
          'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
        )
        .max(100)
        .optional()
        .nullable(),
      startDate: z
        .date()
        .describe(
          'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .optional()
        .nullable(),
      timeZoneId: z
        .string()
        .describe(
          'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
        )
        .max(100)
        .optional()
        .nullable(),
      hideEndDate: z
        .boolean()
        .describe(
          'Whether the end date is hidden in the formatted date and time.'
        )
        .optional()
        .nullable(),
      showTimeZone: z
        .boolean()
        .describe(
          'Whether the time zone is displayed in the formatted schedule.'
        )
        .optional()
        .nullable(),
      recurrenceStatus: z
        .enum([
          'ONE_TIME',
          'RECURRING',
          'RECURRING_UPCOMING',
          'RECURRING_RECENTLY_ENDED',
          'RECURRING_RECENTLY_CANCELED',
        ])
        .describe('Repeating event status.')
        .optional(),
      recurringEvents: z
        .object({
          individualEventDates: z
            .array(
              z.object({
                startDate: z
                  .date()
                  .describe('Event start date.')
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe('Event end date.')
                  .optional()
                  .nullable(),
                timeZoneId: z
                  .string()
                  .describe(
                    'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                showTimeZone: z
                  .boolean()
                  .describe(
                    'Whether the time zone is displayed in a formatted schedule.'
                  )
                  .optional(),
              })
            )
            .max(1000)
            .optional(),
          categoryId: z
            .string()
            .describe(
              'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Event repetitions.')
        .optional(),
      formatted: z
        .object({
          dateAndTime: z
            .string()
            .describe(
              'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
            )
            .max(500)
            .optional()
            .nullable(),
          startDate: z
            .string()
            .describe(
              'Formatted start date of the event. Empty for TBD schedules.'
            )
            .max(500)
            .optional()
            .nullable(),
          startTime: z
            .string()
            .describe(
              'Formatted start time of the event. Empty for TBD schedules.'
            )
            .max(500)
            .optional()
            .nullable(),
          endDate: z
            .string()
            .describe(
              'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
            )
            .max(500)
            .optional()
            .nullable(),
          endTime: z
            .string()
            .describe(
              'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
            )
            .max(500)
            .optional()
            .nullable(),
        })
        .describe('Formatted date and time settings.')
        .optional(),
    })
    .describe('Event date and time settings.')
    .optional(),
  title: z
    .string()
    .describe('Event title.')
    .min(1)
    .max(120)
    .optional()
    .nullable(),
  shortDescription: z
    .string()
    .describe(
      'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .max(500)
    .optional()
    .nullable(),
  detailedDescription: z
    .string()
    .describe('Detailed description of an event.\n\nThis field is deprecated.')
    .max(50000)
    .optional()
    .nullable(),
  mainImage: z
    .string()
    .describe(
      'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .optional(),
  slug: z
    .string()
    .describe(
      'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time when the event was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time when the event was updated.')
    .optional()
    .nullable(),
  status: z
    .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
    .describe('Event status.')
    .optional(),
  registration: z
    .object({
      type: z
        .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
        .describe('Reflects the current state of the registration.')
        .optional(),
      status: z
        .enum([
          'UNKNOWN_REGISTRATION_STATUS',
          'CLOSED_AUTOMATICALLY',
          'CLOSED_MANUALLY',
          'OPEN_RSVP',
          'OPEN_RSVP_WAITLIST_ONLY',
          'OPEN_TICKETS',
          'OPEN_EXTERNAL',
          'SCHEDULED_RSVP',
        ])
        .describe('Registration status.')
        .optional(),
      rsvp: z
        .object({
          responseType: z
            .enum(['YES_ONLY', 'YES_AND_NO'])
            .describe('Available answers for registration to an event.')
            .optional(),
          limit: z
            .number()
            .int()
            .describe('How many guests can RSVP to an event.')
            .optional()
            .nullable(),
          waitlistEnabled: z
            .boolean()
            .describe(
              'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
            )
            .optional(),
          startDate: z
            .date()
            .describe('Registration start date.')
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe('Registration end date.')
            .optional()
            .nullable(),
        })
        .describe('RSVP registration details.')
        .optional(),
      tickets: z
        .object({
          guestsAssignedSeparately: z
            .boolean()
            .describe(
              'Whether the registration form must be filled out separately for each ticket.'
            )
            .optional(),
          ticketLimitPerOrder: z
            .number()
            .int()
            .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
            .min(0)
            .max(50)
            .optional(),
          currency: z
            .string()
            .describe('Ticket price currency.')
            .optional()
            .nullable(),
          lowestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                )
                .optional()
                .nullable(),
            })
            .describe('Lowest ticket price.')
            .optional(),
          highestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                )
                .optional()
                .nullable(),
            })
            .describe('Highest ticket price.')
            .optional(),
          soldOut: z
            .boolean()
            .describe('Whether all tickets are sold for the event.')
            .optional()
            .nullable(),
          taxSettings: z
            .object({
              type: z
                .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                .describe('Tax application settings.')
                .optional(),
              name: z
                .string()
                .describe('Tax name.')
                .min(1)
                .max(10)
                .optional()
                .nullable(),
              rate: z
                .string()
                .describe('Tax rate. For example, `21.55`.')
                .optional()
                .nullable(),
              appliedToDonations: z
                .boolean()
                .describe('Apply tax to donations.')
                .optional()
                .nullable(),
            })
            .describe('How tax is applied.')
            .optional(),
          reservationDurationInMinutes: z
            .number()
            .int()
            .describe('Reservation duration in minutes.')
            .min(5)
            .max(30)
            .optional()
            .nullable(),
        })
        .describe('Ticket registration details.')
        .optional(),
      external: z
        .object({
          url: z
            .string()
            .describe('External event registration URL.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('External registration details.')
        .optional(),
      allowedGuestTypes: z
        .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
        .describe('Types of guests allowed to register.')
        .optional(),
      initialType: z
        .enum(['RSVP', 'TICKETING'])
        .describe(
          "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
        )
        .optional(),
      registrationPaused: z
        .boolean()
        .describe('Whether the registration is paused.')
        .optional(),
      registrationDisabled: z
        .boolean()
        .describe('Whether the registration is disabled.')
        .optional(),
    })
    .describe(
      'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
    )
    .optional(),
  calendarUrls: z
    .object({
      google: z.string().describe('"Add to Google Calendar" URL.').optional(),
      ics: z.string().describe('"Download ICS calendar file" URL.').optional(),
    })
    .describe(
      'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
    )
    .optional(),
  eventPageUrl: z
    .string()
    .describe(
      'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
    )
    .optional(),
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe(
      'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
    )
    .optional(),
  summaries: z
    .object({
      rsvps: z
        .object({
          totalCount: z
            .number()
            .int()
            .describe('Total number of RSVPs.')
            .optional(),
          yesCount: z
            .number()
            .int()
            .describe('Number of RSVPs with status `YES`.')
            .optional(),
          noCount: z
            .number()
            .int()
            .describe('Number of RSVPs with status `NO`.')
            .optional(),
          waitlistCount: z
            .number()
            .int()
            .describe('Number of RSVPs in a waitlist.')
            .optional(),
        })
        .describe('RSVP summary of guests.')
        .optional(),
      tickets: z
        .object({
          ticketsSold: z
            .number()
            .int()
            .describe('Number of sold tickets.')
            .optional(),
          revenue: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Total revenue. Taxes and payment provider fees aren't deducted."
            )
            .optional(),
          currencyLocked: z
            .boolean()
            .describe("Whether the currency is locked and can't be changed.")
            .optional(),
          totalOrders: z
            .number()
            .int()
            .describe('Number of orders placed.')
            .optional(),
          totalSales: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe('Total balance of confirmed transactions.')
            .optional(),
        })
        .describe(
          "Summary of revenue and sold tickets. Archived orders aren't included."
        )
        .optional(),
    })
    .describe(
      'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
    )
    .optional(),
  instanceId: z
    .string()
    .describe('Instance ID of the site where the event is hosted.')
    .optional(),
  guestListSettings: z
    .object({
      displayedPublicly: z
        .boolean()
        .describe('Whether the guest list is displayed publicly to all guests.')
        .optional(),
    })
    .describe('Guest list configuration.')
    .optional(),
  userId: z
    .string()
    .describe('ID of the user who created the event.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  onlineConferencing: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
        )
        .optional(),
      providerId: z
        .string()
        .describe('Conference host ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['MEETING', 'WEBINAR'])
        .describe('Conference type.')
        .optional(),
      session: z
        .object({
          hostLink: z
            .string()
            .describe(
              'Link for the event host to start the online conference session.'
            )
            .optional(),
          guestLink: z
            .string()
            .describe('Link for guests to join the online conference session.')
            .optional(),
          password: z
            .string()
            .describe(
              'Password required to join the online conferencing session (when relevant).'
            )
            .optional()
            .nullable(),
          sessionCreated: z
            .boolean()
            .describe(
              'Whether the session was created successfully on the event host side.'
            )
            .optional()
            .nullable(),
          sessionId: z
            .string()
            .describe('Unique session ID.')
            .optional()
            .nullable(),
        })
        .describe('Online conferencing session information.')
        .optional(),
    })
    .describe(
      'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
    )
    .optional(),
  seoSettings: z
    .object({
      slug: z.string().describe('URL slug.').max(130).optional(),
      advancedSeoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Advanced SEO data.')
        .optional(),
      hidden: z
        .boolean()
        .describe('Whether the slug is hidden from the SEO sitemap.')
        .optional()
        .nullable(),
    })
    .describe(
      'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
    )
    .optional(),
  contactLabel: z
    .string()
    .describe('Assigned contacts label key.')
    .optional()
    .nullable(),
  agendaSettings: z
    .object({
      enabled: z
        .boolean()
        .describe('Whether the schedule is enabled for the event.')
        .optional(),
      pageUrl: z.string().describe('Schedule page URL.').optional(),
    })
    .describe(
      'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
    )
    .optional(),
  eventDisplaySettings: z
    .object({
      hideEventDetailsButton: z
        .boolean()
        .describe(
          'Whether event details button is hidden. Only available for events with no registration.'
        )
        .optional()
        .nullable(),
      hideEventDetailsPage: z
        .boolean()
        .describe(
          'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Visual settings for event.')
    .optional(),
  description: z
    .any()
    .describe(
      'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
    )
    .optional(),
  publishedDate: z
    .date()
    .describe('Event publish timestamp.')
    .optional()
    .nullable(),
});
export const GetEventBySlugRequest = z.object({
  slug: z.string().describe('URL slug.').min(1).max(130),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'DETAILS',
            'TEXTS',
            'REGISTRATION',
            'URLS',
            'FORM',
            'DASHBOARD',
            'FEED',
            'ONLINE_CONFERENCING_SESSION',
            'SEO_SETTINGS',
            'AGENDA',
            'CATEGORIES',
          ])
        )
        .max(20)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const GetEventBySlugResponse = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe(
          'Short description that appears under the event title.\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .max(500)
        .optional()
        .nullable(),
      detailedDescription: z
        .string()
        .describe(
          'Detailed description of an event.\n\nThis field is deprecated.'
        )
        .max(50000)
        .optional()
        .nullable(),
      mainImage: z
        .string()
        .describe(
          'Main event image. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      slug: z
        .string()
        .describe(
          'Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time when the event was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time when the event was updated.')
        .optional()
        .nullable(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
      registration: z
        .object({
          type: z
            .enum(['RSVP', 'TICKETING', 'EXTERNAL', 'NONE'])
            .describe('Reflects the current state of the registration.')
            .optional(),
          status: z
            .enum([
              'UNKNOWN_REGISTRATION_STATUS',
              'CLOSED_AUTOMATICALLY',
              'CLOSED_MANUALLY',
              'OPEN_RSVP',
              'OPEN_RSVP_WAITLIST_ONLY',
              'OPEN_TICKETS',
              'OPEN_EXTERNAL',
              'SCHEDULED_RSVP',
            ])
            .describe('Registration status.')
            .optional(),
          rsvp: z
            .object({
              responseType: z
                .enum(['YES_ONLY', 'YES_AND_NO'])
                .describe('Available answers for registration to an event.')
                .optional(),
              limit: z
                .number()
                .int()
                .describe('How many guests can RSVP to an event.')
                .optional()
                .nullable(),
              waitlistEnabled: z
                .boolean()
                .describe(
                  'Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status.'
                )
                .optional(),
              startDate: z
                .date()
                .describe('Registration start date.')
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe('Registration end date.')
                .optional()
                .nullable(),
            })
            .describe('RSVP registration details.')
            .optional(),
          tickets: z
            .object({
              guestsAssignedSeparately: z
                .boolean()
                .describe(
                  'Whether the registration form must be filled out separately for each ticket.'
                )
                .optional(),
              ticketLimitPerOrder: z
                .number()
                .int()
                .describe('Ticket limit per order. <br>\nDefault: 20 tickets.')
                .min(0)
                .max(50)
                .optional(),
              currency: z
                .string()
                .describe('Ticket price currency.')
                .optional()
                .nullable(),
              lowestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Lowest ticket price.')
                .optional(),
              highestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Highest ticket price.')
                .optional(),
              soldOut: z
                .boolean()
                .describe('Whether all tickets are sold for the event.')
                .optional()
                .nullable(),
              taxSettings: z
                .object({
                  type: z
                    .enum(['INCLUDED_IN_PRICE', 'ADDED_AT_CHECKOUT'])
                    .describe('Tax application settings.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Tax name.')
                    .min(1)
                    .max(10)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe('Tax rate. For example, `21.55`.')
                    .optional()
                    .nullable(),
                  appliedToDonations: z
                    .boolean()
                    .describe('Apply tax to donations.')
                    .optional()
                    .nullable(),
                })
                .describe('How tax is applied.')
                .optional(),
              reservationDurationInMinutes: z
                .number()
                .int()
                .describe('Reservation duration in minutes.')
                .min(5)
                .max(30)
                .optional()
                .nullable(),
            })
            .describe('Ticket registration details.')
            .optional(),
          external: z
            .object({
              url: z
                .string()
                .describe('External event registration URL.')
                .url()
                .optional()
                .nullable(),
            })
            .describe('External registration details.')
            .optional(),
          allowedGuestTypes: z
            .enum(['VISITOR_OR_MEMBER', 'MEMBER'])
            .describe('Types of guests allowed to register.')
            .optional(),
          initialType: z
            .enum(['RSVP', 'TICKETING'])
            .describe(
              "Initial registration type which is set when creating an event.\n\nOnce set, this value is immutable. To change the type of registration, use the `registration.type` field:\n- Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.\n- `RSVP` events can't become `TICKETING` events, and vice versa."
            )
            .optional(),
          registrationPaused: z
            .boolean()
            .describe('Whether the registration is paused.')
            .optional(),
          registrationDisabled: z
            .boolean()
            .describe('Whether the registration is disabled.')
            .optional(),
        })
        .describe(
          'RSVP or ticketing registration details. <br>\n**Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.'
        )
        .optional(),
      calendarUrls: z
        .object({
          google: z
            .string()
            .describe('"Add to Google Calendar" URL.')
            .optional(),
          ics: z
            .string()
            .describe('"Download ICS calendar file" URL.')
            .optional(),
        })
        .describe(
          'URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>\n**Note:** This field is returned only when the `DETAILS` fieldset is included in the request.'
        )
        .optional(),
      eventPageUrl: z
        .string()
        .describe(
          'Event page URL components. <br>\n**Note:** This field is returned only when the `URLS` fieldset is included in the request.'
        )
        .optional(),
      form: z
        .object({
          controls: z
            .array(
              z.object({
                type: z
                  .enum([
                    'INPUT',
                    'TEXTAREA',
                    'DROPDOWN',
                    'RADIO',
                    'CHECKBOX',
                    'NAME',
                    'GUEST_CONTROL',
                    'ADDRESS_SHORT',
                    'ADDRESS_FULL',
                    'DATE',
                  ])
                  .describe('Field control type.')
                  .optional(),
                system: z
                  .boolean()
                  .describe(
                    'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Deprecated: Use `id` or `_id`.')
                  .optional(),
                inputs: z
                  .array(
                    z.object({
                      name: z.string().describe('Field name.').optional(),
                      array: z
                        .boolean()
                        .describe(
                          '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                        )
                        .optional(),
                      label: z
                        .string()
                        .describe('Main field label.')
                        .optional(),
                      additionalLabels: z
                        .record(z.string(), z.string())
                        .describe(
                          'Additional labels for multi-valued fields such as address.'
                        )
                        .optional(),
                      options: z.array(z.string()).optional(),
                      mandatory: z
                        .boolean()
                        .describe('Whether field is mandatory.')
                        .optional(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of accepted characters (relevant for text fields).'
                        )
                        .optional(),
                      type: z
                        .enum([
                          'TEXT',
                          'NUMBER',
                          'TEXT_ARRAY',
                          'DATE_TIME',
                          'ADDRESS',
                        ])
                        .describe(
                          'Type which determines field format.\nUsed to validate submitted response.'
                        )
                        .optional(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                        )
                        .optional()
                        .nullable(),
                      defaultOptionSelection: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z.never().optional(),
                            }),
                            z.object({
                              placeholderText: z.never().optional(),
                              optionIndex: z
                                .number()
                                .int()
                                .describe(
                                  '0-based index from predefined `controls.inputs.options` which is initial selection.'
                                )
                                .min(0)
                                .max(199),
                            }),
                            z.object({
                              optionIndex: z.never().optional(),
                              placeholderText: z
                                .string()
                                .describe(
                                  'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe(
                          'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                        )
                        .optional(),
                      labels: z
                        .array(
                          z.object({
                            name: z.string().describe('Field name.').optional(),
                            label: z
                              .string()
                              .describe('Field label.')
                              .optional(),
                          })
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                label: z
                  .string()
                  .describe('*Deprecated:** Use `controls.inputs.label`.')
                  .optional(),
                orderIndex: z
                  .number()
                  .int()
                  .describe(
                    'Field controls are sorted by this value in ascending order.'
                  )
                  .optional(),
                _id: z.string().describe('Unique control ID.').optional(),
                deleted: z
                  .boolean()
                  .describe('Whether the input control is deleted.')
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
          messages: z
            .object({
              rsvp: z
                .object({
                  rsvpYesOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"YES"`.'
                    )
                    .optional(),
                  rsvpNoOption: z
                    .string()
                    .describe(
                      'Label text indicating RSVP\'s `status` is `"NO"`.'
                    )
                    .optional(),
                  positiveMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                    )
                    .optional(),
                  waitlistMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for positive response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          message: z
                            .string()
                            .describe('Confirmation message text.')
                            .optional(),
                          addToCalendarActionLabel: z
                            .string()
                            .describe(
                              '"Add to calendar" call-to-action label text.'
                            )
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                    )
                    .optional(),
                  negativeMessages: z
                    .object({
                      title: z
                        .string()
                        .describe('Main form title for negative response.')
                        .optional(),
                      confirmation: z
                        .object({
                          title: z
                            .string()
                            .describe('Confirmation message title.')
                            .optional(),
                          shareActionLabel: z
                            .string()
                            .describe(
                              '"Share event" call-to-action label text.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Confirmation messages shown after registration.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                    )
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('"Submit form" call-to-action label text.')
                    .optional(),
                })
                .describe(
                  '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
                )
                .optional(),
              checkout: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for response.')
                    .optional(),
                  submitActionLabel: z
                    .string()
                    .describe('Submit form call-to-action label text.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      downloadTicketsLabel: z
                        .string()
                        .describe(
                          '"Download tickets" call-to-action label text.'
                        )
                        .optional(),
                      addToCalendarLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareEventLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after checkout.')
                    .optional(),
                })
                .describe('Checkout form messages.')
                .optional(),
              registrationClosed: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event registration is closed.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event registration is closed.')
                .optional(),
              ticketsUnavailable: z
                .object({
                  message: z
                    .string()
                    .describe(
                      'Message shown when event tickets are unavailable.'
                    )
                    .optional(),
                  exploreEventsActionLabel: z
                    .string()
                    .describe(
                      '"Explore other events" call-to-action label text.'
                    )
                    .optional(),
                })
                .describe('Messages shown when event tickets are unavailable.')
                .optional(),
            })
            .describe(
              'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
            )
            .optional(),
        })
        .describe(
          'Event registration form. <br>\n**Note:** This field is returned only when the `FORM` fieldset is included in the request.'
        )
        .optional(),
      summaries: z
        .object({
          rsvps: z
            .object({
              totalCount: z
                .number()
                .int()
                .describe('Total number of RSVPs.')
                .optional(),
              yesCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `YES`.')
                .optional(),
              noCount: z
                .number()
                .int()
                .describe('Number of RSVPs with status `NO`.')
                .optional(),
              waitlistCount: z
                .number()
                .int()
                .describe('Number of RSVPs in a waitlist.')
                .optional(),
            })
            .describe('RSVP summary of guests.')
            .optional(),
          tickets: z
            .object({
              ticketsSold: z
                .number()
                .int()
                .describe('Number of sold tickets.')
                .optional(),
              revenue: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Total revenue. Taxes and payment provider fees aren't deducted."
                )
                .optional(),
              currencyLocked: z
                .boolean()
                .describe(
                  "Whether the currency is locked and can't be changed."
                )
                .optional(),
              totalOrders: z
                .number()
                .int()
                .describe('Number of orders placed.')
                .optional(),
              totalSales: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Total balance of confirmed transactions.')
                .optional(),
            })
            .describe(
              "Summary of revenue and sold tickets. Archived orders aren't included."
            )
            .optional(),
        })
        .describe(
          'Summary of RSVP or ticket sales. <br>\n**Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.'
        )
        .optional(),
      instanceId: z
        .string()
        .describe('Instance ID of the site where the event is hosted.')
        .optional(),
      guestListSettings: z
        .object({
          displayedPublicly: z
            .boolean()
            .describe(
              'Whether the guest list is displayed publicly to all guests.'
            )
            .optional(),
        })
        .describe('Guest list configuration.')
        .optional(),
      userId: z
        .string()
        .describe('ID of the user who created the event.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      onlineConferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests.'
            )
            .optional(),
          providerId: z
            .string()
            .describe('Conference host ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['MEETING', 'WEBINAR'])
            .describe('Conference type.')
            .optional(),
          session: z
            .object({
              hostLink: z
                .string()
                .describe(
                  'Link for the event host to start the online conference session.'
                )
                .optional(),
              guestLink: z
                .string()
                .describe(
                  'Link for guests to join the online conference session.'
                )
                .optional(),
              password: z
                .string()
                .describe(
                  'Password required to join the online conferencing session (when relevant).'
                )
                .optional()
                .nullable(),
              sessionCreated: z
                .boolean()
                .describe(
                  'Whether the session was created successfully on the event host side.'
                )
                .optional()
                .nullable(),
              sessionId: z
                .string()
                .describe('Unique session ID.')
                .optional()
                .nullable(),
            })
            .describe('Online conferencing session information.')
            .optional(),
        })
        .describe(
          'Online conferencing details. <br>\n**Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.'
        )
        .optional(),
      seoSettings: z
        .object({
          slug: z.string().describe('URL slug.').max(130).optional(),
          advancedSeoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Advanced SEO data.')
            .optional(),
          hidden: z
            .boolean()
            .describe('Whether the slug is hidden from the SEO sitemap.')
            .optional()
            .nullable(),
        })
        .describe(
          'SEO settings. <br>\n**Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.'
        )
        .optional(),
      contactLabel: z
        .string()
        .describe('Assigned contacts label key.')
        .optional()
        .nullable(),
      agendaSettings: z
        .object({
          enabled: z
            .boolean()
            .describe('Whether the schedule is enabled for the event.')
            .optional(),
          pageUrl: z.string().describe('Schedule page URL.').optional(),
        })
        .describe(
          'Event schedule details. <br>\n**Note:** This field is returned only when the `AGENDA` fieldset is included in the request.'
        )
        .optional(),
      eventDisplaySettings: z
        .object({
          hideEventDetailsButton: z
            .boolean()
            .describe(
              'Whether event details button is hidden. Only available for events with no registration.'
            )
            .optional()
            .nullable(),
          hideEventDetailsPage: z
            .boolean()
            .describe(
              'Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page.'
            )
            .optional()
            .nullable(),
        })
        .describe('Visual settings for event.')
        .optional(),
      description: z
        .any()
        .describe(
          'Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>'
        )
        .optional(),
      publishedDate: z
        .date()
        .describe('Event publish timestamp.')
        .optional()
        .nullable(),
    })
    .describe('Event.')
    .optional(),
});
